import {STORIES} from '../dummy_data/sampleStories';
import {DIAGNOSTIC_DATA} from '../dummy_data/reports/sample_diagnostic_report';
import Amplify, { API, Analytics, Storage, graphqlOperation, Auth } from 'aws-amplify';
import {poll} from '../library';
import studentRecordService from './StudentRecordService';
import studentInformationService from './StudentInformationService';
import {getRegionalURL, searchSessionAudio} from './SpeechStream';
import {
  userTypes,
  assignmentTypes,
  activityStatus,
  recommendationModes,
  locales,
  phraseTypes
} from '../constants/constants';
import { calculateAccuracy, getNonFlaggedErrors } from '../components/Reader/ReaderLibrary.js';
import * as Sentry from '@sentry/browser';
import {prefetchWordMetadata} from "./WordMetadataService.js";
import {setStudentGradeLevelWithAREA} from "./readerCharacteristics.js";
import { statusType, getActivityStatus } from "./displayStatus";
import {getProgressMonitoringStory} from "./progressMonitoring.js";
import {
	formatGuidForSIS,
	isNotNullOrUndefined,
	logSentryException,
	removeEmphasisTags,
	removeOpeningBreakTag,
	removeOpeningEllipsis,
	removeProsodyTags,
  isEnglishLocale,
  isSingleSpokenWord,
	sanitizeDataForSentry,
	sortActivitiesByCreatedDate,
	parseTextWithSpellOutTag,
  validateBlobURL
} from './util';
import {isiPad} from './environment';
import device from './device/device';
import { logSessionInfo, storeUtteranceLog } from './logger';
import {filterByDate, filterByLocale, getSchoolYear} from '../components/Reports/util/validation';
import {formatLocale_UpperCaseHyphen, formatLocale_UpperCaseUnderscore} from './locale';
import {inBenchmarkScreeningWindow, inCurrentScreeningWindow, getScreeningWindow} from './screeningWindowService';
import { uuidToHEX } from './util';
import { isAsrCheckStoryId } from '../constants/asrCheckStories';
import { getNextEarlyReaderStory, isEarlyReaderInSequence } from 'Services/EarlyReaderService';
import { getAudioForPhonemes } from './phonemeToAudio';
import { getAudioForSyllable } from './syllableToAudio';
import {withTimeout} from './util.js';
import {ASSIGNMENT_TYPE} from "Services/assignment/constants";
import { getSaxonSequenceStart } from './saxonService';
import { getPhrasesPerPage } from './APIUtils';
import { fixActivitiesParenting } from './APIFixParentChildRelationship.js';
import get from 'lodash.get';
import { isScorableEarlyReaderStory } from './EarlyReaderService';
import getArmScore from '../components/Reports/util/getArmScore';
import { GetStoryBranding, SetBranding, isGenerativeTTSDisabledBrand } from './BrandingService';
import { redirectToLoginOrigin, wouldRedirectToLoginOrigin } from './loginService.js';
import { TTS_ENGINE_TYPE } from './generativeVoiceService.js';
import { generateVisemesFromTextAndAudio, createRandomSpeechMarks } from './visemeGenerator.js';
import i18n from '../i18n/i18n.js';
import CalendarService from './CalendarService.js';
import { FEATURE_FLAGS } from '../constants/featureFlags.js';
import { isBTSEffective } from './entitlementService';

export const DEFAULT_LOCALE = "en-us";
const MAX_RETRIES = 3;

// Login-time activity history (saveUserData). One shared read of the newest scored assessment and
// tutor rows replaces the two scored-only fetches that used to run per login for them; the tutor
// read is TT-991's and is the only one that needs non-scored statuses and the audioNetworkSignals
// payload. Partner rows are read separately, and only for the grades whose ERSS check consumes
// them, so they cannot crowd assessment/tutor rows out of the shared window.
const LOGIN_HISTORY_LIMIT = 15;
// How many rows setV2InterventionUserParams and setUserTheta walk. Five is what the dedicated read
// they used to have asked for: enough to walk back past a 0-WCPM latest row to the student's last
// real score (TT-1289 / CR-10540), and the limit AE-13418 shipped the walker with. The shared read
// is wider so it can also count storiesRead; the walkers are sliced back to this so the rows they
// see are the rows they always saw.
const INTERVENTION_WALK_LIMIT = 5;
// storiesRead is compared with `> 5` (cursor-free eligibility) and `< 1` (the cursor-free FTUE
// variant), both in CursorOrchestrator, so six is where the count can stop.
const STORIES_READ_CAP = 6;
// The ASR-check drop happens client-side, after this limit: SRS has no "storyId not in" filter.
// Completed mic checks are archived and never in the window, but an abandoned one (about one row
// in seven, prod2 as of 2026-09) stays at not_started and takes a slot, so the real prior
// activity is found only if fewer than this many abandoned checks are newer than it. Ten is the
// derivation from that rate: nine abandoned checks in a row before the real story is lost, at a
// rate of one in seven, is far enough into the tail to be nobody. Do not lower this.
const TUTOR_HISTORY_LIMIT = 10;
const SCORED_STATUSES = [statusType.SCORED, statusType.RESCORED];
// Every status a tutor row is seen in except `archived`, which on prod is carried only by completed
// ASR-check stories. Explicit because SRS defaults an absent status filter to scored/rescored.
// `assigned` is the muted-abort status (displayStatus `muted`), the closest thing SRS has to a
// labelled dead-mic session; `rescored` is a displayStatus in practice but harmless here. No
// `aborted`: that is a displayStatus, and no tutor row carried it as a status in 30 days of prod.
// No `rescoring` or `stuck_placement` either: zero tutor rows in the 90 days of prod2 activities_v
// to 2026-09-14 (scored 9.68M, in_progress 2.51M, not_started 1.10M, assigned 109K, scoring 2.3K,
// nothing else). Point-in-time measurements, not invariants.
//
// `underReview` is in that same zero-row set and IS listed, which is not an oversight: statusType
// spells it camelCase where every status SRS actually returns is snake_case, so it cannot match a
// row today and costs nothing in an OR filter. Kept as the one entry that would start matching if
// SRS ever emitted the status its own statusType already names. The census justifies not chasing
// values statusType does not define; it does not prune values it does.
const TUTOR_HISTORY_STATUSES = [
  statusType.NOT_STARTED,
  statusType.IN_PROGRESS,
  statusType.SCORING,
  statusType.SCORED,
  statusType.RESCORED,
  statusType.ASSIGNED,
  statusType.UNDER_REVIEW,
];
const ENGLISH_SINGLE_WORD_PREVIOUS_TEXT = 'The word is: ';

// Capture URL params at module load time before they are stripped from the URL
const _initialUrlParams = new URLSearchParams(window.location.search);
const _isSpanishPilot = _initialUrlParams.get('isSpanishPilot') === 'true';

const DEFAULT_USER_STORE_DATA = {
  userId: null,
  sessionId: null,
  userType: null,
  userAgent: null,
  sessionStartTime: null,
  sessionData: {},
};

var stories_store = [];
var utterance_store = {};
var utterance_inflight = {};  // Tracks in-flight TTS requests by key to deduplicate concurrent calls
var userData_store = {...DEFAULT_USER_STORE_DATA};

const defaultSORConfig = {
  enableERPictures: true,
  enablePracticePictures: true,
  enableGiveWord: true,
  enableFandP: false,
  enableDRA: false,
  enableLexile: false,
  enableExtendedColumns: false,
  selectedStandard: "COMMON_CORE",
  interventionFrequency: "STANDARD",
  customARMLevelMapping: null,
  instructSyllableInterventionArmThreshold: 1.5,
};

// Set this to true to get a dump of all the word metadata in the console when you hit "read a story"
// NOTE: we should never deploy it this way - it will take a lot of time on the "choose a story" screen.
export const RUN_METADATA_DUMP = false;

if(RUN_METADATA_DUMP){
  // Story dump - remember to add chapter to the graphQL
  studentRecordService.getRecommendedStories("",3,'tutor',100,"")
  .then((stories)=>{
    stories.map((story)=>{
      let content = story.chapters[0].phrases.reduce((total,phrase)=>{
        return total += ' ' + phrase;
      },"");
    });
  })
  .catch(err=>{console.log("ERROR IN RECOMMEND:");throw new Error(err);});
}

//Set this to true in order to load the stories from sampleStories.js
const GET_STORIES_LOCALLY = false;

//Set this to true in order to load a hard coded assignment for all students, entering forced assessment mode
const FORCE_ASSESSMENT = process.env.FORCE_ASSESSMENT;

//Delete userData_store
export function clearUserData(){
  console.log("trying to sign out");
  userData_store = {...DEFAULT_USER_STORE_DATA};
  Auth.signOut()
  .catch(err => console.error("ERROR SIGNING OUT",err));
}

//return the userData_store
export function getUserData(){
  return userData_store;
}

/**
 * The raw `tutorLocale` URL param value, preserved before bilingual mode overwrites
 * `userData.locale` to `es-mx`. Null when the param was absent (non-URL-launched sessions).
 * @returns {string|null}
 */
export function getRawTutorLocale() {
  return getUserData().rawTutorLocale || null;
}

/**
 * Returns true if isSpanishPilot=true URL param is set.
 * Post-BTS (ASSMNT-1126): This flag now primarily gates the Spanish pilot story pool.
 * Other pilot features (generative TTS, new PM, AOS) have been transitioned to default Spanish behavior.
 */
export function getIsSpanishPilot(){
  return _isSpanishPilot;
}

/**
 * Returns whether district or global app settings enable the new Spanish PM path for this mode.
 * Assessment mode suppresses district/app-setting enablement but not explicit Spanish pilot sessions.
 */
export function isNewSpanishPlacementManagerLicenseActive(assessmentMode=false){
  return !assessmentMode && (userData_store.appSettings?.newSpanishPlacementManagerEnabled === true ||
    userData_store.newSpanishPlacementManagerEnabled === true
  );
}

/**
 * Returns whether the current session should use new Spanish PM behavior.
 * Requires a Spanish locale. Enablement differs by session type:
 *   - Tutor: USE_NEW_SPANISH_PM alone enables the new Spanish PM (AmiraPlacementManager) for
 *     all es-mx districts, legacy (non-BTS-licensed) included. This was needed once NRT stopped
 *     running Google server-side, which broke the legacy Google NRT tutor path.
 *   - Assessment: the flag is AND'd with the evaluarBTS license again, so legacy districts stay
 *     on the legacy Google assessment path (GooglePlacementManager) and only BTS-licensed
 *     districts get the new PM in assessment.
 * Kill switches: flip USE_NEW_SPANISH_PM off to disable fleet-wide, or set the per-district
 * forceNewSpanishASROff app setting. The _isSpanishPilot / district-license fallback below is
 * only reached when the (session-appropriate) flag gate is off.
 *
 * `spanishContent` stands in for the es-mx locale for Spanish content in a non-Spanish session
 * (the Spanish ASR check, GT-413); every other gate still applies.
 */
export function shouldUseNewSpanishPlacementManagerForCurrentSession(assessmentMode=false, { spanishContent = false } = {}){
  if (!spanishContent && getUserData().locale !== 'es-mx') return false;
  if (getUserData().appSettings?.forceNewSpanishASROff === true) return false;

  if (FEATURE_FLAGS.USE_NEW_SPANISH_PM) return true;

  return _isSpanishPilot || isNewSpanishPlacementManagerLicenseActive(assessmentMode);
}

/**
 * Whether Spanish utterances should use the generative TTS engine for the given locale.
 * Non-es-mx locales always return false (English generative is controlled separately via useGenerative).
 * For es-mx: true when USE_NEW_SPANISH_VOICE is on and the brand allows generative TTS, or when isSpanishPilot URL param is set.
 * ASSMNT-1799: USE_NEW_SPANISH_VOICE is AND'd with the evaluarBTS license (isBTSEffective) — the
 * flag is a fleet-wide kill switch, not an independent enable, so it never bypasses a missing license.
 */
export function shouldUseNewSpanishVoice(locale){
  if (locale.toLowerCase() !== 'es-mx') return false;

  return (isBTSEffective() && FEATURE_FLAGS.USE_NEW_SPANISH_VOICE && !isGenerativeTTSDisabledBrand()) || _isSpanishPilot;
}

export function setUserData(key, value){
  userData_store[key] = value;
}

export function setSessionActivityId(inActivityId, assessmentMode){
  userData_store.activityId = inActivityId;
  userData_store.assessmentMode = assessmentMode;

  //Initialize the inference service session
  if(!assessmentMode && process.env.INFERENCE_SERVICE_URL && process.env.INFERENCE_SERVICE_EAST_2){
    const baseURL = getUserData().region && getUserData().region === 'us-east-2' ? process.env.INFERENCE_SERVICE_EAST_2 : process.env.INFERENCE_SERVICE_URL;
    const url =  baseURL + "/init/" + inActivityId;
    const requestBody = {
      isESL: getUserData().isESL ?? false,
    }
    if (getUserData().interventionSelectionWCPMScore) {
      requestBody.prev_equatedwcpm = getUserData().interventionSelectionWCPMScore;
    }
    return fetch(url,{
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody),
      mode: 'no-cors'
    })
    .catch((err)=>console.error("Error priming inference for activity",inActivityId, err));
  }
}

export const defaultConfig = {
  NWF_CONFIG: [false,true,true,false,false,false,false,false,false,false],
  LISTENING_COMP_CONFIG: [false,false,false,false,false,false,false,false,false,false],
  READING_COMP_CONFIG: [false,false,false,false,true,true,true,true,true,false],
  SPELLING_CONFIG: [false,false,false,false,false,false,false,false,false,false],
  LETTERNAME_CONFIG: [false,false,false,false,false,false,false,false,false,false],
  VOCABULARY_SCREENER_CONFIG: [false,false,false,false,false,false,false,false,false,false],
  LETTER_NAMING_AND_SOUNDS_CONFIG: [false,false,false,false,false,false,false,false,false,false],
};

/**
 * Fetches application settings from entitlements service (district 9999999 - general settings)
 * 
 * Note: For students, district-specific settings are also fetched from their actual district/school.
 * The app checks both sources and uses either if enabled (OR logic).
 * 
 * Available license keys:
 * - siteUnderMaintenance: Redirects users to maintenance page when true
 * - useStruggleMonitor: Enables/disables struggle monitoring system
 * - assessmentEnabled: Controls assessment feature availability
 * - screenerEnabled: Controls screener feature availability
 * - expandedInterventionSuppression: When enabled, applies the 30-50 WCPM intervention suppression
 *   logic to users below 30 WCPM (including undefined WCPM). This means users below 30 WCPM will
 *   skip interventions when 3+ UNKs are detected, and give word interventions when the current
 *   phrase already has a word intervention. Users 30+ WCPM continue with their normal logic.
 *   Can be enabled at either the general (9999999) level or district-specific level.
 * - newSpanishPlacementManagerEnabled: Enables the new Spanish Placement Manager behavior for tutor
 *   sessions without enabling unrelated Spanish pilot behavior. Assessment sessions keep the legacy
 *   Spanish assessment path.
 * - midAssessmentAudioIssueTroubleshootEnabled: When true at the config (9999999) level, enables the
 *   mid-assessment audio troubleshoot flow for districts that have not explicitly disabled it. A
 *   district-level setting of false takes precedence and disables the flow regardless of config.
 * - enableBrowserASR: When true, enables on-device (browser) ASR. Can also appear on the district
 *   license; either source being true satisfies the license check. Because of that OR, setting it
 *   false on a district record is a no-op while the global record is true — to opt a district out,
 *   use forceBrowserASROff on its record instead.
 * - forceBrowserASROff: When true, overrides enableBrowserASR and disables browser ASR entirely.
 *   Can also appear on the district license, so a single district can be opted out while the
 *   global enableBrowserASR rollout stays on; either source being true disables it (CR-10679).
 * - filterStoryOnGradeLimits: Enables grade-limit filtering on story selection. The district license
 *   value is used when set (including an explicit false); otherwise falls back to the 9999999 value.
 *   forceFilterStoryOnGradeLimitsOff is intended to override both.
 * - forceTapWordOff: When true, disables tap-word functionality.
 * - forceCursorFreeOff: When true, prevents cursor-free mode from activating regardless of WCPM.
 * - forceNewSpanishASROff: When true, disables the new Spanish Placement Manager path.
 * - forceFilterStoryOnGradeLimitsOff: When true, disables grade-limit filtering on story selection.
 * - forceBadAudioSignalOff / forceBadNetworkSignalOff: When true, that signal (bad audio / bad
 *   network) can no longer make a NEW bad-signal cursor decision (GT-306); the other signal still
 *   can, and students already placed in cursor-free or tap-to-read keep their mode. Either source
 *   being true turns it off, so a district-level false cannot undo a global true. Read once at
 *   login, so a flip takes effect on the student's next launch.
 * 
 * @returns {Promise<Object>} Application settings object with license key flags
 */
async function getAppSettings(){
  return await studentRecordService.getEntitlements(9999999,9999999);
}

export function setV2InterventionUserParams(activities) {
  let interventionSelectionWCPMScore;
  let interventionSelectionARMScore;
  let resolvedWCPM;

  if (activities) {
    for (let i = 0; i < activities.length; i++) {
      const activity = activities[i];
      const scores = activity.scores;
      const scoreEstimates = activity.scoreEstimates;

      // Intervention model input (sent as prev_equatedwcpm): the Kalman estimate, then the
      // measured equated score. Both are on the equated scale the model's thresholds are
      // calibrated against (the 30/50 suppression bands in ReaderManager, wcpmPivot in the
      // ranked intervention selector), so equatedWCPM is the value the parameter is named for.
      // Raw wcpmScore is deliberately NOT used here - it is not grade-equated.
      // Anything left unresolved falls back to interventionFallbackWCPM, the grade population
      // mean, which is a worse guess than this student's own measured score on the same activity.
      // A WCPM of 0 is not a meaningful reading speed — skip it like undefined (TT-1247).
      if (interventionSelectionWCPMScore == null) {
        if (scoreEstimates && scoreEstimates.WCPM_estimate > 0) {
          interventionSelectionWCPMScore = scoreEstimates.WCPM_estimate;
        } else if (scores && scores.equatedWCPM > 0) {
          interventionSelectionWCPMScore = scores.equatedWCPM;
        }
      }

      // General WCPM (cursor-free gating, struggling-reader classification, aiService
      // features.student.WCPM): same resolution, plus raw wcpmScore as a last resort.
      // A WCPM of 0 is not a meaningful reading speed — skip it like undefined so the
      // student falls through to the grade-level default (TT-1247).
      if (resolvedWCPM == null) {
        if (scoreEstimates && scoreEstimates.WCPM_estimate > 0) {
          resolvedWCPM = scoreEstimates.WCPM_estimate;
        } else if (scores && scores.equatedWCPM > 0) {
          resolvedWCPM = scores.equatedWCPM;
        } else if (scores && scores.wcpmScore > 0) {
          resolvedWCPM = scores.wcpmScore;
        }
      }

      if (interventionSelectionARMScore == null && scoreEstimates && scoreEstimates.ARM_estimate != null) {
        interventionSelectionARMScore = scoreEstimates.ARM_estimate;
      }

      if (interventionSelectionWCPMScore != null && interventionSelectionARMScore != null && resolvedWCPM != null) {
        break;
      }
    }
  }

  if (interventionSelectionWCPMScore != null) {
    userData_store.interventionSelectionWCPMScore = interventionSelectionWCPMScore;
  }

  if (resolvedWCPM != null) {
    userData_store.WCPM = resolvedWCPM;
  }

  if (interventionSelectionARMScore != null) {
    userData_store.interventionSelectionARMScore = interventionSelectionARMScore;
    // Also set the general ARM score from the same estimate
    userData_store.ARM = interventionSelectionARMScore;
  }

  // Set fallback WCPM score based on student's actual grade
  userData_store.interventionFallbackWCPM = getDefaultScores(userData_store.actualGrade).wcpmScore;
  
  // Set fallback ARM score based on student's actual grade
  userData_store.interventionFallbackARM = getDefaultScores(userData_store.actualGrade).armScore;
}

function isScoredRow(row) {
  return row?.status === statusType.SCORED || row?.status === statusType.RESCORED;
}

// Newest row by createdAt, ignoring rows without one. SRS already orders createdAt desc; sorting
// a handful of rows costs nothing and removes the dependence on that.
function latestByCreatedAt(rows) {
  if (!Array.isArray(rows)) {
    return undefined;
  }
  // Parseable dates only, the same predicate buildNormalizedSignal applies to measuredAt: an
  // unparseable createdAt would make the comparator return NaN and the "newest" row arbitrary.
  return [...rows]
    .filter((row) => row && Number.isFinite(Date.parse(row.createdAt)))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
}

/**
 * The login-time answer to checkForAnyCompletedActivity, derived from rows saveUserData already
 * holds instead of a sixth SRS read (GQL_CHECK_SCORED, 30 rows). Same three rules: any scored
 * assessment or tutor; a tutor whose displayStatus is completed but that the pipeline has not
 * scored; any row downleveled to prereader. Two-state on purpose — true, or null for "unknown" —
 * never false: the rows on hand are the newest scored ones plus a short tutor window, so their
 * silence is not evidence (a prereader assessment can sit at not_started, older than anything
 * here), and a wrong false replays the FTUE and forces the tutor. hasAnyCompletedActivity runs
 * the network read on null, which is exactly the population the old code read for anyway.
 *
 * Every row counts by the status it carries, exactly as the network read does, whichever read it
 * came from. A partially scored assessment (score_pass < 1) is selected by the scored filter but
 * comes back rewritten to in_progress, and the network read keys on that rewritten status; so does
 * this, so that student falls through to the same network read and the same answer they got
 * before. Counting scored-read rows by presence would be a product change (no forced first tutor
 * story, no FTUE) riding an unrelated consolidation.
 *
 * One answer is deliberately not a subset of the network read's: that read takes the newest 30
 * rows in ANY of six statuses, so a student with more than 30 abandoned rows since their last
 * scored one gets false from it, while the scored-only window here reaches past the abandoned
 * rows and answers true. The student has completed an activity; true is the correct answer.
 */
function deriveAnyCompletedActivity(...rowSets) {
  const isAssessmentOrTutor = (row) => row?.type === assignmentTypes.ASSESSMENT || row?.type === assignmentTypes.TUTOR;
  const rows = rowSets
    .flatMap((set) => (Array.isArray(set) ? set : []))
    .filter(isAssessmentOrTutor);
  const completed = rows.some((row) => isScoredRow(row)
    || (row.type === assignmentTypes.TUTOR && row.displayStatus === statusType.COMPLETED)
    || row.displayStatus === statusType.PREREADER);
  return completed ? true : null;
}

/**
 * Finds and sets the user's most recent theta score from their activity history.
 * Loops through activities to find the first one with a defined theta score.
 * @param {Array} activities - Array of student activities from studentRecordService
 */
export function setUserTheta(activities) {
  if (!activities || activities.length === 0) {
    return;
  }
  
  for (const activity of activities) {
    if (activity.scores && activity.scores.theta !== undefined && activity.scores.theta !== null) {
      userData_store.THETA = activity.scores.theta;
      return;
    }
  }
  // If no theta found, leave userData_store.THETA undefined
}

/**
 * Save user data with exponential backoff retry logic
 * 
 * @param {Object} authData - Authentication data
 * @param {number} retries - Number of retries remaining (default: MAX_RETRIES)
 * @param {number} backoff - Base backoff delay in milliseconds (default: 1000ms)
 * @returns {Promise} Promise that resolves with user data or rejects after all retries fail
 * 
 * Retry delays follow exponential backoff with jitter:
 * - Attempt 1: 1000ms + 0-500ms jitter
 * - Attempt 2: 2000ms + 0-500ms jitter  
 * - Attempt 3: 4000ms + 0-500ms jitter
 */
export async function saveUserData(authData,retries=MAX_RETRIES,backoff=1000){
  // Safeguard against excessive retry values
  if (retries > MAX_RETRIES) retries = MAX_RETRIES;
  // if authData doesn't have a userType, or the userType is invalid, we will default to STUDENT,
  // on the other hand, if the userType does exist, then we assign the entire authData to userData_store.
  // for now all routes are exposed for user type STUDENT
  const urlParams = new URLSearchParams(window.location.search);
  let iPad = isiPad();

  return Auth.currentUserInfo()
  .then(async resp => {
    userData_store = {
      userId: resp.attributes['custom:sisId'] || resp.attributes.sub,
      sessionId: getSessionID(),
      userType: resp.attributes['custom:userType'] || userTypes.STUDENT,
      userAgent: navigator.userAgent,
      sessionStartTime: Date.now(),
      firstName: resp.attributes['custom:firstName'], //TODO: pull name from SIS
      lastName: resp.attributes['custom:lastName'],
      email: resp.attributes.email,
      userName: resp.username,
      browser: device.getBrowserForSession(),
      sessionData: {
        cognitoUserSession: authData.signInUserSession,
        authData: authData,
        isiPad: iPad,
        useIpadFallbacks: iPad && parseFloat(device.browser.version) < 13,
      },
      assessment: resp.attributes['custom:assessment'],
      skipMagic: resp.attributes.skipMagic,
      correlationid: resp.attributes.correlationid,
      selectedTutorSkin: resp.attributes['custom:selectedTutorSkin'],
      selectedTutorRig: resp.attributes['custom:selectedTutorRig']
    };

    //Special case for side-loaded admins
    if(!resp.attributes['custom:sisId']){
      userData_store.skipMagic = true;
      userData_store.sideloadedUser = true;
    }

    let connection = {}
    if(navigator.connection){
      connection = {
        downlink: navigator.connection.downlink,
        effectiveType: navigator.connection.effectiveType,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      }
    }

    userData_store.connection = connection;
    console.log(JSON.stringify(getUserData().connection));

    if(FEATURE_FLAGS.ENABLE_SENTRY_USER_LOGGING){
      Sentry.configureScope((scope) => {
        scope.setUser(sanitizeDataForSentry(userData_store));
        scope.setTag("user-id", userData_store.userId);
        scope.setTag("user-type", userData_store.userType);
        scope.setTag("user-school", userData_store.schoolId);
        scope.setTag("user-district", userData_store.districtId);
        });
    }
    console.log(process.env.gitHash);
    logSessionInfo("app_version",{gitHash: COMMITHASH});

    //NUCLEAR OPTION: Redirect the user to the site maintenance page
    userData_store.appSettings = await getAppSettings();
    if(userData_store.appSettings && userData_store.appSettings.siteUnderMaintenance){
      underMaintenance();
    }


    if(userData_store.userType === userTypes.STUDENT){
      userData_store.rawTutorLocale = urlParams.get('tutorLocale') || null;
      userData_store.locale = urlParams.get('tutorLocale') || (_isSpanishPilot ? 'es-mx' : DEFAULT_LOCALE);
      userData_store.screenerOverrides = {};

      //Get the URL for the speech
      const regionInfo = await getRegionalURL(authData.region);
      if(regionInfo){
        userData_store.speechStreamURL = regionInfo.url;
        userData_store.region =  regionInfo.region;
      }

      //Get student metatata
      let studentInfo = await studentInformationService.getStudent(userData_store.userId);
      if(studentInfo && studentInfo.metadata){
        userData_store.sisGrade = studentInfo.metadata.actual_grade;
        userData_store.isESL = studentInfo.metadata.esl;
        userData_store.hasSeenFTUE = studentInfo.metadata.hasSeenFTUE;
        userData_store.hasSeenDialogFTUE = studentInfo.metadata.hasSeenDialogFTUE;
        userData_store.ftueStatuses = studentInfo.metadata.ftueStatuses;
        userData_store.badSignalAltCursorMode = studentInfo.metadata.badSignalAltCursorMode ?? null;
        userData_store.tutorInSpanish = studentInfo.metadata.tutorInSpanish;
        userData_store.assessmentLanguageOverride = studentInfo.metadata.assessmentLanguageOverride;
        userData_store.selectedTutorRig = studentInfo.metadata.selectedTutorRig;
        userData_store.selectedTutorSkin = studentInfo.metadata.selectedTutorSkin;
        userData_store.nonverbal = studentInfo.metadata.nonverbal;
        userData_store.extendAssessmentTime = studentInfo.metadata.extendAssessmentTime;
      }else{
        userData_store.isESL = false;
        userData_store.hasSeenFTUE = true;
        userData_store.badSignalAltCursorMode = null;
        userData_store.tutorInSpanish = false;
        userData_store.nonverbal = false;
        userData_store.extendAssessmentTime = false;
      }

      if(userData_store.isESL){
        userData_store.assessmentPageNavEnabled = true; //We always want the next button with ESL kids
      }

      if(userData_store.tutorInSpanish){
        userData_store.locale = userData_store.tutorInSpanish ? 'es-mx' : DEFAULT_LOCALE;
        userData_store.bilingualLocale = 'es-mx';
        userData_store.bilingualMode = true;
      }

      const studentId = getFormatedId(userData_store.userId);
      // TT-991: guaranteed defaults. Both are derived further down, inside a block that is
      // conditional and wrapped in a try/catch, while their consumers (cursor/priorActivitySignals,
      // hasAnyCompletedActivity) must never have to tell "not fetched" apart from "nothing found".
      userData_store.priorActivitySignals = null;
      userData_store.tutorHistorySignals = null;
      userData_store.anyCompletedActivity = null;
      return Promise.all([
        // One read replaces two. It carries the scored assessment+tutor rows that
        // setV2InterventionUserParams and setUserTheta walk (sliced to INTERVENTION_WALK_LIMIT
        // below), that the AREA/decode seed and the ERSS "any history" check read, and the scored
        // tutor rows behind storiesRead. Newest first from SRS. Partner rows are NOT in here: the
        // window has no date bound, and a student with years of NWEA rows would crowd the
        // assessment/tutor rows the walkers depend on out of it.
        //
        // `equated: true` because the limit-5 read this replaces passed it, and both things the
        // flag does have to happen server-side to be right: SRS drops rows with no storyId BEFORE
        // applying the limit, so the walkers can never be starved of story-bearing rows by
        // storyless ones ahead of them in the window, and the Activity.scores resolver reports
        // equatedWCPM as wcpmScore, which is the number the walker's last-resort branch expects.
        // The cost is that storyless scored rows are invisible to storiesRead and testsTaken too.
        // On prod that cost is nil: over the 90 days of prod2 activities_v to 2026-09-14, no scored
        // assessment or tutor row had a null storyId (970,913 assessments, 9,677,945 tutor rows).
        studentRecordService.studentActivityHistory(studentId, {
          limit: LOGIN_HISTORY_LIMIT,
          activityType: [assignmentTypes.ASSESSMENT, assignmentTypes.TUTOR],
          status: SCORED_STATUSES,
          equated: true,
        }),
        // TT-991: the student's most recent real tutor stories in ANY non-archived status, with the
        // TT-1256 payload. The status list is what the review of #6004 caught: the old read was
        // scored-only, and on prod (2026-09) 28% of real tutor activities never reach scored — a dead mic or
        // a muted abort leaves the row at not_started / in_progress / assigned — so the population
        // the bad-signal branch exists to catch could never become "the prior activity". Archived
        // is omitted because completed ASR checks are the only tutor rows that carry it; abandoned
        // ASR checks are dropped by storyId below. No .catch on either read: studentActivityHistory
        // never rejects, it logs and resolves undefined, which the destructure below treats as
        // "read failed".
        //
        // Deliberately ungated: the consumer runs only behind badSignalCursorEnabled / log-only, but
        // the license that carries those loads later in this chain, and the dark launch wants the
        // payload from every district anyway. Once the dark launch concludes, this is the read to
        // gate (at the cost of serializing it behind the license read).
        studentRecordService.studentActivityHistory(studentId, {
          limit: TUTOR_HISTORY_LIMIT,
          activityType: [assignmentTypes.TUTOR],
          status: TUTOR_HISTORY_STATUSES,
          withSignals: true,
        }),
      ]).then(async ([scoredHistory, tutorHistory]) => {
        // Defensive: studentActivityHistory resolves undefined/non-array on a swallowed error.
        const sharedReadFailed = !Array.isArray(scoredHistory);
        // SRS orders createdAt desc on both of its listActivities paths, and develop's walker
        // consumed rows in that server order. Sorting a handful of rows costs nothing and removes
        // the dependence, the same stance latestByCreatedAt takes. Unparseable dates sort last
        // rather than being dropped: a row with a bad timestamp still carries a valid score.
        const sortKey = (row) => {
          const t = Date.parse(row.createdAt);
          return Number.isFinite(t) ? t : -Infinity;
        };
        // Compared, not subtracted: two -Infinity sentinels subtracted give NaN, and a NaN from a
        // comparator makes Array#sort implementation-defined.
        const newestFirst = (a, b) => {
          const ta = sortKey(a);
          const tb = sortKey(b);
          return tb > ta ? 1 : tb < ta ? -1 : 0;
        };
        const scoredRows = sharedReadFailed ? [] : scoredHistory.filter(Boolean).sort(newestFirst);
        // SRS applies `limit` after the status filter, so a window with fewer rows than the limit
        // is the student's entire scored history. Only a full window (or a failed read) leaves
        // room for rows the dedicated fallbacks below could find.
        const windowMayBeTruncated = sharedReadFailed || scoredRows.length >= LOGIN_HISTORY_LIMIT;
        // Sliced to the limit the dedicated equated read used to ask for, so the walkers see
        // exactly the rows, and the numbers, they always did.
        const activities = scoredRows.slice(0, INTERVENTION_WALK_LIMIT);
        const scoredTutorRows = scoredRows.filter((row) => row.type === assignmentTypes.TUTOR);
        const scoredAssessmentRows = scoredRows.filter((row) => row.type === assignmentTypes.ASSESSMENT);
        // null, not [], when the read failed: "unknown" must stay distinguishable from "no stories".
        // Archived rows are kept out by TUTOR_HISTORY_STATUSES, which is explicit precisely because
        // SRS defaults an absent status filter to scored/rescored; no second predicate here.
        const tutorHistoryRows = Array.isArray(tutorHistory)
          ? tutorHistory.filter((row) => row && !isAsrCheckStoryId(row.storyId))
          : null;
        return (resp.attributes.skipMagic ? Promise.resolve({grade: resp.attributes.grade, districtId: resp.attributes.districtId, schoolId: resp.attributes.schoolId}) : getStudentInfo(resp.username))
          .then(async info =>{
            userData_store.actualGrade = info.grade > 8 ? 8 : info.grade;

            // Fallback: if the primary query returned no scored activities and
            // the student is in the ERSS-eligible grade range, check for
            // activities still in the scoring pipeline. This prevents a race
            // condition where a just-completed assessment has not yet been
            // scored and the student gets incorrectly forced into ERSS.
            const isErssEligibleGrade = (
              userData_store.actualGrade === -1 ||
              userData_store.actualGrade === 0 ||
              userData_store.actualGrade === 1
            );
            // "No scored history" is only knowable when the read that would have shown it
            // succeeded. When it failed, activities is [] because nothing was looked at, and the
            // ERSS default below must not run: it would persist early_reader: true to a student who
            // may have years of scored stories, and nothing un-sets that flag until the student
            // reads out of the early-reader sequence. One SRS blip must not re-place a reader.
            const noScoredActivities = !sharedReadFailed && (!activities || activities.length === 0);
            if (isErssEligibleGrade && sharedReadFailed) {
              logSessionInfo('erss_override_skipped_read_failed', { studentId });
            }
            // The NWEA partner row only matters to the ERSS default, and only when the student has
            // no scored assessment or tutor history of their own (both consumers below are gated on
            // that), so the read for it runs only for a PK-1 student with none — same filter and
            // limit as before, out of the shared window.
            let partnerActivityList = [];
            if (isErssEligibleGrade && noScoredActivities) {
              // No .catch: studentActivityHistory never rejects, and the isArray guard is what
              // handles the undefined a failed read resolves to.
              const partnerRows = await studentRecordService.studentActivityHistory(studentId, {
                limit: 1,
                activityType: ["partner"],
                status: SCORED_STATUSES,
              });
              partnerActivityList = Array.isArray(partnerRows) ? partnerRows.filter(Boolean) : [];
            }
            let hasPendingScoringActivity = false;
            const noScoredPartnerActivities = partnerActivityList.length === 0;
            if (isErssEligibleGrade && noScoredActivities && noScoredPartnerActivities) {
              // Existence checks only, so the light login document: the GQL_STUDENT_ACTIVITIES
              // read these used to go through drags children, story.chapters.items, student and the
              // audioNetworkSignals batch resolver for a row whose only use is `.length`. No
              // try/catch: studentActivityHistory never rejects, and a failed read (undefined) reads
              // as "nothing pending", which is what the old swallowed error resolved to as well.
              const pendingScoringStatuses = [statusType.NOT_STARTED, statusType.IN_PROGRESS, statusType.SCORING];
              const [pendingActivities, pendingPartnerActivities] = await Promise.all([
                studentRecordService.studentActivityHistory(studentId, {
                  limit: 1,
                  activityType: [assignmentTypes.ASSESSMENT, assignmentTypes.TUTOR],
                  status: pendingScoringStatuses,
                }),
                studentRecordService.studentActivityHistory(studentId, {
                  limit: 1,
                  activityType: ["partner"],
                  status: pendingScoringStatuses,
                }),
              ]);
              hasPendingScoringActivity =
                (Array.isArray(pendingActivities) && pendingActivities.length > 0) ||
                (Array.isArray(pendingPartnerActivities) && pendingPartnerActivities.length > 0);
            }

            if (hasPendingScoringActivity) {
              logSessionInfo('erss_override_skipped_pending_activity', { studentId });
            }

            let assignedEarlyReader = studentInfo.metadata.early_reader;
            let assignedSpanishEarlyReader = studentInfo.metadata.spanish_early_reader;

            const hasPartnerActivity = partnerActivityList.length > 0;
            // For grade PK, K, and 1, default to ERSS if user has no assessment/tutor history and is not NWEA Partner
            // Handle case where activities might be undefined or null (grade check first: most logins are grade 2+)
            // Also skip the override if there are activities still in the scoring pipeline (hasPendingScoringActivity).
            if (
              isErssEligibleGrade &&
              !hasPartnerActivity &&
              noScoredActivities &&
              !hasPendingScoringActivity
            ) {
              assignedEarlyReader = true;
              if (userData_store.isESL) {
                assignedSpanishEarlyReader = true;
              }
            }

            // Debug-only ERSS override (URL params `forceErssEnglish` / `forceErssSpanish`):
            // force the student into early-reader (ERSS) mode for this session WITHOUT persisting
            // to student metadata. Applied to the assigned* flags here — before getNextEarlyReaderStory —
            // so the real ERSS sequence stories are fetched exactly as they would be for a truly
            // assigned early reader; the early_reader / spanish_early_reader metadata write below is
            // then skipped (forceErssActive) so the backend values are never changed. Lets a tester
            // exercise English/Spanish ERSS without editing the student record.
            const forceErssEnglish = urlParams.get('forceErssEnglish') === 'true';
            const forceErssSpanish = urlParams.get('forceErssSpanish') === 'true';
            const forceErssActive = forceErssEnglish || forceErssSpanish;
            if (forceErssEnglish) {
              assignedEarlyReader = true;
            }
            if (forceErssSpanish) {
              assignedSpanishEarlyReader = true;
            }

            //early reader status — null means ERSS was unreachable; [] means it
            //answered authoritatively with no stories. Only persist a metadata change
            //when we got an authoritative answer (TT-1420).
            const erssResult = await getNextEarlyReaderStory(userData_store.userId, assignedEarlyReader, assignedSpanishEarlyReader);
            const erssUnavailable = erssResult === null;
            userData_store.sequenceStartStories = erssResult ?? [];
            //coerce to boolean: metadata.early_reader can be null for the pre-fix cohort, and a bare
            //null/undefined value here would either mis-compare against stored null or vanish from the
            //GraphQL mutation entirely (JSON.stringify drops undefined keys) instead of writing false/true
            //
            //When ERSS is unavailable, trust the assigned flag directly — don't let an
            //empty sequence override it. Otherwise a new PK/K/1 student whose auto-assign
            //fires (line ~755) but whose first login hits an ERSS outage would get
            //isEarlyReader=false, read regular content, score a story, and the auto-assign
            //never fires again — permanently locked out of ERSS. (TT-1420)
            let isEarlyReader = erssUnavailable
              ? Boolean(assignedEarlyReader)
              : Boolean(assignedEarlyReader && isEarlyReaderInSequence(userData_store.sequenceStartStories,'en-us'));
            let isSpanishEarlyReader = erssUnavailable
              ? Boolean(assignedSpanishEarlyReader)
              : Boolean(assignedSpanishEarlyReader && isEarlyReaderInSequence(userData_store.sequenceStartStories,'es-mx'));

            //persist early reader / spanish early reader settings together (i.e. if they finished the sequence)
            //send only the changed keys: updateStudentMetadata merges against fresh server state, so sending
            //the whole stale studentInfo.metadata snapshot here would revert any other field (esl, tutorInSpanish,
            //saxon, assessmentLanguageOverride, ...) that changed server-side during this login's earlier awaits
            //compare against the boolean-coerced stored value, not the raw one: metadata.early_reader is
            //null for the pre-fix cohort, and comparing null to a coerced boolean would fire a write for
            //nearly every student on their first post-deploy login, clobbering the null backfill marker (ASSMNT-2098)
            const earlyReaderChanged = Boolean(studentInfo.metadata.early_reader) !== isEarlyReader;
            const spanishEarlyReaderChanged = Boolean(studentInfo.metadata.spanish_early_reader) !== isSpanishEarlyReader;
            // Skip the metadata write entirely when a debug ERSS override is active so the forced
            // mode never writes early_reader / spanish_early_reader back to the student record.
            // When ERSS is unavailable, only persist promotions (false/null → true), not
            // demotions (true → false). The auto-assign for new PK/K/1 students must persist
            // even during an outage; a transient failure must not permanently demote a real
            // early reader. (TT-1420)
            //
            // The two guards below cannot currently fire: during an outage isEarlyReader is
            // Boolean(assignedEarlyReader) (above), and assignedEarlyReader only ever moves
            // false → true via the auto-assign, so "changed" implies a promotion. They are kept as
            // defense-in-depth for the day something else can lower the assigned flag, and are
            // deliberately not test-covered — the reachable protection is the ternary above, which
            // API.test.js "ERSS outage (TT-1420)" pins.
            if((earlyReaderChanged || spanishEarlyReaderChanged) && !forceErssActive){
              const metadataChanges = {};
              if(earlyReaderChanged){
                if(!erssUnavailable || isEarlyReader){
                  metadataChanges.early_reader = isEarlyReader;
                }
              }
              if(spanishEarlyReaderChanged){
                if(!erssUnavailable || isSpanishEarlyReader){
                  metadataChanges.spanish_early_reader = isSpanishEarlyReader;
                }
              }
              if(Object.keys(metadataChanges).length > 0){
                await studentInformationService.updateStudentMetadata(userData_store.userId, metadataChanges);
                logSessionInfo("early_reader_change", {
                  studentId: userData_store.userId,
                  from: { early_reader: studentInfo.metadata.early_reader, spanish_early_reader: studentInfo.metadata.spanish_early_reader },
                  to: { early_reader: isEarlyReader, spanish_early_reader: isSpanishEarlyReader },
                  erssUnavailable,
                });
              }
            }

            userData_store.isEarlyReader = isEarlyReader;
            userData_store.isSpanishEarlyReader = isSpanishEarlyReader;

            setV2InterventionUserParams(activities);
            setUserTheta(activities);

            let lastActivity = null;
            if(activities && activities.length > 0){

              lastActivity = activities[0];
              const scores = lastActivity.scores;
              
              if (scores) {
                if (scores.areaScore){
                  userData_store.grade = setStudentGradeLevelWithAREA(scores.areaScore);
                  userData_store.AREA = scores.areaScore;
                }

                if (scores.decodeScore || scores.decodeScore === 0) {
                  userData_store.decodeScore = scores.decodeScore;
                }
              }
            }

            // Check for interventionSelectionWCPM URL parameter override
            const urlWCPM = urlParams.get('interventionSelectionWCPM');
            if (urlWCPM !== null && !isNaN(parseFloat(urlWCPM))) {
              userData_store.WCPM = parseFloat(urlWCPM);
            }

            // Check for interventionSelectionARM URL parameter override
            const urlARM = urlParams.get('interventionSelectionARM');
            if (urlARM !== null && !isNaN(parseFloat(urlARM))) {
              userData_store.ARM = parseFloat(urlARM);
            }

            // Check for forceUserTheta URL parameter override
            const urlTheta = urlParams.get('forceUserTheta');
            if (urlTheta !== null && !isNaN(parseFloat(urlTheta))) {
              userData_store.THETA = parseFloat(urlTheta);
            }

            userData_store.grade = userData_store.grade ? userData_store.grade : userData_store.actualGrade;

            if (userData_store.AREA === null || userData_store.AREA === undefined) {
              userData_store.AREA = getDefaultScores(userData_store.grade).areaScore;
            }

            if (userData_store.decodeScore === null || userData_store.decodeScore === undefined) {
              userData_store.decodeScore = getDefaultScores(userData_store.grade).decodeScore;
            }

            if (userData_store.WCPM === null || userData_store.WCPM === undefined) {
              userData_store.WCPM = getDefaultScores(userData_store.grade).wcpmScore;
            }

            if (userData_store.ARM === null || userData_store.ARM === undefined) {
              userData_store.ARM = getDefaultScores(userData_store.grade).armScore;
            }

            //Check for Saxon sequence start
            let isSaxonReader = studentInfo.metadata.saxon;
            if(!isEarlyReader && isSaxonReader){
              userData_store.sequenceStartStories = await getSaxonSequenceStart(userData_store.userId,isSaxonReader,userData_store.actualGrade,userData_store.sequenceStartStories);
            }
            if(userData_store.sequenceStartStories.length){
              userData_store.startingEarlyReaderSequence = true;
            }
            userData_store.isSaxonReader = isSaxonReader;
            userData_store.districtId = info.districtId;
            userData_store.schoolId = info.schoolId;
            Sentry.setTag("school-id", userData_store.schoolId);
            Sentry.setTag("district-id", userData_store.districtId);
            if(info.districtId && info.schoolId){
              const schoolId = info.schoolId;
              const districtId = info.districtId;
              
              // Fetch assessment calendar (for assessment school year), school year calendar, and entitlements in parallel to reduce wait time
              const [assessmentCalendar, schoolYearCalendar, entitlements] = await Promise.all([
                CalendarService.getAssessmentCalendar(districtId, schoolId),
                CalendarService.getCurrentSchoolYearCalendar(districtId, schoolId),
                studentRecordService.getEntitlements(districtId, schoolId)
              ]);
              
              // Store assessment school year from assessment calendar (this is what we use for assignments)
              if (assessmentCalendar && assessmentCalendar.schoolYear != null) {
                userData_store.schoolYear = assessmentCalendar.schoolYear;
              }
              
              // Store school year calendar dates if available
              if(schoolYearCalendar){
                userData_store.schoolYearStartDate = schoolYearCalendar.startDate;
                userData_store.schoolYearEndDate = schoolYearCalendar.endDate;
              }

              // storiesRead / testsTaken for cursor-free eligibility, derived from the login read
              // above instead of two more fetches. CursorOrchestrator gates on `storiesRead > 5`,
              // `storiesRead < 1` and `testsTaken > 0`, so storiesRead is capped where the old
              // limit-6 fetch capped it and testsTaken keeps the 0/1 range of the old limit-1 fetch.
              // logCursorFreeModeStatus EMITS both values into the cursor_free_mode_enabled session
              // log, so a value this window cannot know is recorded as null, never as a guessed 0.
              try {
                let storiesRead = Math.min(scoredTutorRows.length, STORIES_READ_CAP);
                // A full window with fewer than six tutor rows in it is the one case the shared read
                // cannot answer: the tutor rows may just be older than the assessments that filled
                // it. A failed shared read is the other. Fall back to the dedicated count, as before,
                // so one SRS hiccup cannot zero a count that used to have a read of its own.
                // Hoisted so deriveAnyCompletedActivity can read these rows too: on a failed shared
                // read they may be the only scored rows in hand.
                let tutorCountRows = [];
                if (windowMayBeTruncated && storiesRead < STORIES_READ_CAP) {
                  // Same filter as the shared read, so the two paths to this number agree.
                  const tutorRows = await studentRecordService.studentActivityHistory(studentId, {
                    limit: STORIES_READ_CAP,
                    activityType: [assignmentTypes.TUTOR],
                    status: SCORED_STATUSES,
                    equated: true,
                  });
                  // studentActivityHistory never rejects: a failed read resolves undefined, an
                  // array is an answer. Collapsing both to [] would zero a count the shared window
                  // already put a floor under, and the failure lands where it does the most damage.
                  // A full window holding under six tutor rows is ten-plus assessments, so that
                  // student's testsTaken is 1 -- and the failed read never reaches the catch below
                  // to zero it too. testsTaken 1 with storiesRead 0 is the exact shape
                  // shouldShowCursorFreeFTUE reads as "has tested, has never read a story"
                  // (testsTaken > 0 && storiesRead < 1), so one SRS hiccup would hand the
                  // never-read-a-story FTUE to a student with scored tutors in hand. Keeping the
                  // floor is wrong only in being low, never the 0 that means "no stories".
                  // (On the sharedReadFailed path the floor is 0 because nothing is known, which is
                  // what the dedicated read this replaced also answered. Unchanged.)
                  if (Array.isArray(tutorRows)) {
                    tutorCountRows = tutorRows.filter(Boolean);
                    storiesRead = Math.min(tutorCountRows.length, STORIES_READ_CAP);
                  }
                }

                // Assessments keep a read of their own only for a failed shared read. A short
                // window with no assessment in it is the whole scored history, so 0 is exact. A full
                // window with none in it is fifteen scored tutor stories, and then testsTaken gates
                // nothing: both gates (cursor-free eligibility, the cursor-free FTUE variant) are
                // settled by storiesRead alone once it is above five. But the value is also logged,
                // and a heavy tutor reader's older assessment is real, so that case is null,
                // "unknown", not a 0 the log would take at face value. The gates read null as 0.
                // The fallback read matches the one it replaces in limit and status, plus
                // `equated: true` so it sees the same rows as the shared read. Counted by row
                // rather than by status: SRS rewrites a partially scored assessment's status to
                // in_progress on the way out (score_pass < 1), and the old read counted that row.
                // testsTaken counts this row by presence (a partially scored assessment, rewritten
                // to in_progress on the way out, is still a test taken) while
                // deriveAnyCompletedActivity below counts the same row by status (it is not yet a
                // completed activity). Both match the reads they replace; the asymmetry is deliberate.
                const windowIsFull = !sharedReadFailed && scoredRows.length >= LOGIN_HISTORY_LIMIT;
                let testsTaken = scoredAssessmentRows.length > 0 ? 1 : (windowIsFull ? null : 0);
                let assessmentHistoryRows = [];
                if (sharedReadFailed) {
                  const rows = await studentRecordService.studentActivityHistory(studentId, {
                    limit: 1,
                    activityType: [assignmentTypes.ASSESSMENT],
                    status: SCORED_STATUSES,
                    equated: true,
                  });
                  assessmentHistoryRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
                  testsTaken = assessmentHistoryRows.length ? 1 : 0;
                }

                userData_store.studentMetrics = { storiesRead, testsTaken };

                // TT-991: the prior real tutor activity, consumed at cursor-decision time by
                // src/services/cursor/priorActivitySignals.js. Newest by createdAt rather than by
                // position, over the non-archived, non-ASR-check rows sliced above.
                //
                // Stashed as the TT-1256 payload plus the three record fields the reader and the
                // decision log need — createdAt so the reader can refuse a signal older than
                // badSignalMaxSignalAgeDays, status/displayStatus so the dark-launch log can
                // stratify scored prior activities from abandoned ones — and not the whole row.
                // Null-tolerant on purpose: a row written by a client older than #5982 carries no
                // payload, and fromStoredActivity reads a missing schemaVersion as "no signal".
                //
                // The newest row is taken even when it has no payload (the payload is written at
                // phrase ends, so a story opened and quit before its first phrase has none), and the
                // read does NOT fall back to an older row that has one. "Prior activity" means the
                // last one by design, and the older row is not lost: the login that came before this
                // one already classified it, latching a mode when live and logging a decision row
                // when dark. Falling back would re-classify the same session on every login after an
                // abandoned one.
                const latestTutorActivity = latestByCreatedAt(tutorHistoryRows);
                userData_store.priorActivitySignals = latestTutorActivity?.audioNetworkSignals
                  ? {
                    createdAt: latestTutorActivity.createdAt,
                    audioNetworkSignals: latestTutorActivity.audioNetworkSignals,
                    activityStatus: latestTutorActivity.status ?? null,
                    displayStatus: latestTutorActivity.displayStatus ?? null,
                  }
                  : null;

                // GT-306: the SAME rows, all of them, for TT-1377's student-level bad-network rule
                // (cursor/networkHistorySignals). That rule needs the set — distinct high-RTT days
                // and the share of sessions — where the audio rule needs only the newest. No new
                // read: this is the tutor-history-with-signals payload already in hand, trimmed to
                // the payload plus the fields the rule and the log need. Rows without a payload
                // are dropped here (nothing to classify); rows without ack timings are kept and
                // excluded from the denominator by the rule itself, so the log can count them.
                // Launch-only by design: nothing in-session appends to this list.
                userData_store.tutorHistorySignals = Array.isArray(tutorHistoryRows)
                  ? tutorHistoryRows
                    .filter((row) => Number.isFinite(row?.audioNetworkSignals?.schemaVersion))
                    .map((row) => ({
                      createdAt: row.createdAt,
                      audioNetworkSignals: row.audioNetworkSignals,
                      activityStatus: row.status ?? null,
                    }))
                  : null;

                userData_store.anyCompletedActivity = deriveAnyCompletedActivity(
                  scoredRows, tutorCountRows, assessmentHistoryRows, tutorHistoryRows,
                );
              } catch (error) {
                console.error('Failed to fetch student activity counts:', error);
                userData_store.studentMetrics = {
                  storiesRead: 0,
                  testsTaken: 0,
                };
                userData_store.priorActivitySignals = null;
                userData_store.tutorHistorySignals = null;
                userData_store.anyCompletedActivity = null;
              }

              const resp = entitlements;
              //If there is no entitlement, default to true. This is pretty fundamental to not having a single point
              // of failure for users. It's better to give them more than to have everybody go down.
              if(resp){
                // Set the branding here so its avaialable across the app for students
                SetBranding(resp.branding);

                userData_store.assessmentEnabled = ((resp.assessmentEnabled !== null) && (resp.assessmentEnabled !== undefined)) ? resp.assessmentEnabled : true;
                userData_store.branding = resp.branding;
                userData_store.partner = resp.partner;
                userData_store.assessmentPageNavEnabled = resp.assessmentPageNavEnabled || true;
                userData_store.tutorEnabled = resp.tutorEnabled;
                userData_store.calibrationEnabled = resp.calibrationEnabled;
                userData_store.demoMode = resp.demoMode;
                userData_store.demoLockdown = resp.demoLockdown;
                userData_store.dyslexiaEnabled = resp.dyslexiaEnabled;
                userData_store.dyslexiaParentLetterEnabled = resp.dyslexiaParentLetterEnabled;
                userData_store.parentReportEnabled = ((resp.parentReportEnabled !== null) && (resp.parentReportEnabled !== undefined)) ? resp.parentReportEnabled : true;
                userData_store.nwfConfig = resp.nwfConfig || defaultConfig.NWF_CONFIG;
                userData_store.listeningCompConfig = resp.listeningCompConfig || defaultConfig.LISTENING_COMP_CONFIG;
                userData_store.readingCompConfig = resp.readingCompConfig || defaultConfig.READING_COMP_CONFIG;
                userData_store.spellingConfig = resp.spellingConfig || defaultConfig.SPELLING_CONFIG;
                userData_store.letternameConfig = resp.letternameConfig || defaultConfig.LETTERNAME_CONFIG;
                userData_store.vocabularyScreenerConfig = resp.vocabularyScreenerConfig || defaultConfig.VOCABULARY_SCREENER_CONFIG;
                userData_store.letterNamingAndSoundsConfig = resp.letterNamingAndSoundsConfig || defaultConfig.LETTER_NAMING_AND_SOUNDS_CONFIG;
                userData_store.dyslexiaTestEnabled = ((resp.dyslexiaTestEnabled !== null) && (resp.dyslexiaTestEnabled !== undefined)) ? resp.dyslexiaTestEnabled : true;
                userData_store.screenerEnabled = ((resp.screenerEnabled !== null) && (resp.screenerEnabled !== undefined)) ? resp.screenerEnabled : true;
                userData_store.autoAssign = resp.autoAssign || false;
                userData_store.screeningStartDate = resp.screeningStartDate;
                userData_store.screeningEndDate = resp.screeningEndDate;
                userData_store.tutorWhitelist = resp.tutorWhitelist;
                userData_store.iesEnabled = resp.iesEnabled;
                userData_store.districtAdminBadgingEnabled = resp.districtAdminBadgingEnabled;
                userData_store.expandedEarlyReaderEnabled = resp.expandedEarlyReaderEnabled;
                userData_store.expandedInterventionSuppression = resp.expandedInterventionSuppression;
                userData_store.newSpanishPlacementManagerEnabled = resp.newSpanishPlacementManagerEnabled === true;
                userData_store.sorConfig = resp.sorConfig || defaultSORConfig;
                userData_store.enablePretest = ((resp.enablePretest !== null) && (resp.enablePretest !== undefined)) ? resp.enablePretest : true;
                userData_store.voiceOverrides = resp.voiceOverrides || [];
                userData_store.badgingEnabled = resp.badgingEnabled || false;
                userData_store.showBadNetworkResponse = resp.showBadNetworkResponse || false;
                userData_store.curriculum = resp.curriculum;
                userData_store.allowedIpadBrowsers = resp.allowedIpadBrowsers || [];
                userData_store.ignoreBrowserCompatibilityCheck = resp.ignoreBrowserCompatibilityCheck || false;
                userData_store.assessmentTimerMultiplier = resp.assessmentTimerMultiplier || "1.5";
                userData_store.state = resp.state || null;
                userData_store.amiraIsipVisibility = resp.amiraIsipVisibility || false;

                // Find the first curriculum that includes the user's grade
                const userGrade = userData_store.actualGrade;
                if (userGrade !== undefined && resp.curriculum) {
                  const matchingCurriculum = resp.curriculum.find(
                    curriculum => curriculum.grades && curriculum.grades.includes(userGrade)
                  );

                  if (matchingCurriculum) {
                    userData_store.curriculumId = matchingCurriculum.curriculumId;
                  }
                }

                userData_store.automaticInstructAssignments = resp.automaticInstructAssignments || [];
                userData_store.automaticTextSetAssignments = resp.automaticTextSetAssignments || [];
                userData_store.filterStoryOnGradeLimits = (resp.filterStoryOnGradeLimits ?? userData_store.appSettings?.filterStoryOnGradeLimits) === true;
                userData_store.midAssessmentAudioIssueTroubleshootEnabled = resp.midAssessmentAudioIssueTroubleshootEnabled;
                userData_store.prohibitedRigs = resp.amiraTutorSettings?.prohibitedRigs || [];
                // Convert prohibitedSkins to a dictionary with rig as the key and an array of skins as the value
                userData_store.prohibitedSkins = {};
                if (resp.amiraTutorSettings?.prohibitedSkins) {
                  resp.amiraTutorSettings.prohibitedSkins.forEach(skinString => {
                    const [rig, skin] = skinString.split('.');
                    if (!userData_store.prohibitedSkins[rig]) {
                      userData_store.prohibitedSkins[rig] = [];
                    }
                    userData_store.prohibitedSkins[rig].push(skin);
                  });
                }
                userData_store.tutorSelectionEnabled = resp.amiraTutorSettings?.tutorSelectionEnabled || false;
                userData_store.cursorFreeTutorWCPMThreshold = resp.cursorFreeTutorWCPMThreshold;

                let types = Object.keys(assignmentTypes);
                let formOverrides = {}
                types.map(type=>formOverrides[type] = []);
                userData_store.formOverrides = resp.formOverrides ? resp.formOverrides : formOverrides;
                userData_store.spanishConfig = resp.spanishConfig || "BOTH_ONE_SESSION";
                userData_store.useStruggleMonitor = (userData_store.appSettings && userData_store.appSettings.useStruggleMonitor) || true;
                userData_store.windowDisabledByGrade = resp.windowDisabledByGrade;
                userData_store.subscriptions = resp.subscriptions;
                // Per-district BTS license. Gates Spanish-through-AOS (ASSMNT-1702); see entitlementService.isBTSEffective.
                userData_store.evaluarBTS = resp.evaluarBTS ?? null;
                userData_store.enableBrowserASR = resp.enableBrowserASR === true;
                userData_store.forceBrowserASROff = resp.forceBrowserASROff === true;
                userData_store.enableTapToReadTutor = resp.enableTapToReadTutor;
                // TT-991 bad-signal cursor: the district's own values, read by
                // cursor/signalThresholds with appSettings (district 9999999) as the fallback,
                // the same precedence as cursorFreeTutorWCPMThreshold. Copied as-is, undefined
                // included, so a district that never set one falls through to appSettings and
                // an explicit false blocks it. The dark launch itself is not here: it is the
                // BAD_SIGNAL_LOG_ONLY build constant. GQL_GET_LICENSING selects all five off the
                // AmiraLicenses type (plus GT-306's two kill switches below); a district that never
                // set one comes back undefined and falls through to appSettings. The two threshold
                // groups arrive as JSON text (String on the schema) or an object --
                // cursor/signalThresholds.mergeThresholds accepts both.
                userData_store.badSignalCursorEnabled = resp.badSignalCursorEnabled;
                userData_store.badSignalWcpmPivot = resp.badSignalWcpmPivot;
                userData_store.badSignalAudioThresholds = resp.badSignalAudioThresholds;
                userData_store.badSignalNetworkThresholds = resp.badSignalNetworkThresholds;
                userData_store.badSignalMaxSignalAgeDays = resp.badSignalMaxSignalAgeDays;
                // GT-306's per-signal kill switches, copied as-is like the five above. Unlike them, a
                // true on EITHER this record or appSettings switches the signal off
                // (cursor/signalThresholds.getBadSignalKillSwitches).
                userData_store.forceBadAudioSignalOff = resp.forceBadAudioSignalOff;
                userData_store.forceBadNetworkSignalOff = resp.forceBadNetworkSignalOff;
                userData_store.sensitivityTags = resp.sensitivityTags || [];
              }else{
                userData_store.assessmentEnabled = true;
                userData_store.assessmentPageNavEnabled = true;
                userData_store.tutorEnabled = true;
                userData_store.calibrationEnabled = false;
                userData_store.demoMode = false;
                userData_store.demoLockdown = false;
                userData_store.dyslexiaEnabled = true;
                userData_store.dyslexiaParentLetterEnabled = false;
                userData_store.parentReportEnabled = true;
                userData_store.nwfConfig = defaultConfig.NWF_CONFIG;
                userData_store.listeningCompConfig = defaultConfig.LISTENING_COMP_CONFIG;
                userData_store.readingCompConfig = defaultConfig.READING_COMP_CONFIG;
                userData_store.spellingConfig = defaultConfig.SPELLING_CONFIG;
                userData_store.letternameConfig = defaultConfig.LETTERNAME_CONFIG;
                userData_store.vocabularyScreenerConfig = defaultConfig.VOCABULARY_SCREENER_CONFIG;
                userData_store.letterNamingAndSoundsConfig = defaultConfig.LETTER_NAMING_AND_SOUNDS_CONFIG;
                userData_store.dyslexiaTestEnabled = true;
                userData_store.screenerEnabled = true;
                userData_store.autoAssign = false;
                userData_store.spanishConfig = "BOTH_ONE_SESSION";
                userData_store.sorConfig = defaultSORConfig;
                userData_store.enablePretest = true;
                userData_store.voiceOverrides = [];
                userData_store.windowDisabledByGrade = null;
                userData_store.ignoreBrowserCompatibilityCheck = false;
                userData_store.filterStoryOnGradeLimits = userData_store.appSettings?.filterStoryOnGradeLimits === true;
                userData_store.midAssessmentAudioIssueTroubleshootEnabled = false;
                userData_store.enableBrowserASR = false;
                userData_store.forceBrowserASROff = false;

                let types = Object.keys(assignmentTypes);
                let formOverrides = {}
                types.map(type=>formOverrides[type] = []);
                userData_store.formOverrides = formOverrides;
                userData_store.useStruggleMonitor = true;
              }

              //If the student is not ESL the configuration is always ENGLISH_ONLY
              if(!userData_store.isESL){
                userData_store.spanishConfig = "ENGLISH_ONLY"
              }

              //Once we have all the "real" information on the student, check to see if any overrides are necessary
              if(userData_store.demoMode  || (process.env.IGNORE_STUDENT_HISTORY === 'TRUE')){
                userData_store.grade = userData_store.actualGrade;
                let defaultScores = getDefaultScores(userData_store.grade);
                userData_store.AREA = defaultScores.areaScore;
                userData_store.decodeScore = defaultScores.decodeScore;
              }
              if(studentInfo){
                userData_store.tutorEnabled = ((studentInfo.metadata.tutorEnabled !== undefined) && (studentInfo.metadata.tutorEnabled !== null)) ? studentInfo.metadata.tutorEnabled : userData_store.tutorEnabled;
                userData_store.assessmentEnabled = ((studentInfo.metadata.assessmentEnabled !== undefined) && (studentInfo.metadata.assessmentEnabled !== null)) ? studentInfo.metadata.assessmentEnabled : userData_store.assessmentEnabled;
                userData_store.screenerEnabled = ((studentInfo.metadata.screenerEnabled !== undefined) && (studentInfo.metadata.screenerEnabled !== null)) ? studentInfo.metadata.screenerEnabled : userData_store.screenerEnabled;
                console.log(userData_store)
              }

              if (userData_store.appSettings) {
                //Apply any additional app-level overrides here (you need to use strict equality here. It's possible that these could be null or undefined)
                if(userData_store.appSettings.assessmentEnabled === false){
                  userData_store.assessmentEnabled = false;
                }

                if(userData_store.appSettings.screenerEnabled === false){
                  userData_store.screenerEnabled = false;
                }
              }

              return Promise.resolve(userData_store);
            }else{
              //If there's nothing in licensing, allow both
              userData_store.assessmentEnabled = true;
              userData_store.tutorEnabled = true;
              return Promise.resolve(userData_store);
            }
          });
      });
    }else{
      return studentInformationService.getSchoolsAndDistricts(resp,authData,resp.username,userData_store.userId,userData_store.userType,resp.attributes.skipMagic)
      .then((resp)=>{
        userData_store.permissions = resp.permissions;
        userData_store.reportingRole = resp.reportingRole;
        userData_store.schools = resp.schools;
        userData_store.district = resp.district;
        userData_store.districtId = resp.district.id;
        userData_store.subdistrictId = resp.subdistrictId;
        userData_store.schoolId = resp.schools[0].id;
        Sentry.setTag("school-id", userData_store.schoolId);
        Sentry.setTag("district-id", userData_store.districtId);
        return studentRecordService.getEntitlements(resp.district.id,resp.schools[0].id)
        .then((entitlements)=>{
          // Set the branding here so its avaialable across the app for non-students
          SetBranding(entitlements ? entitlements.branding : null);
          i18n.changeLanguage(entitlements.reportingLanguage || "en");
          userData_store.reportingLanguage = entitlements?.reportingLanguage || "en";
          userData_store.dyslexiaEnabled = entitlements ? entitlements.dyslexiaEnabled : true;
          userData_store.branding = entitlements ? entitlements.branding : null;
          userData_store.partner = entitlements ? entitlements.partner : "Amira";
          userData_store.dyslexiaParentLetterEnabled = entitlements ? entitlements.dyslexiaParentLetterEnabled : false;
          userData_store.parentReportEnabled = entitlements ? (entitlements.parentReportEnabled !== false) : true;
          userData_store.texasResources = entitlements ? entitlements.texasResources : false;
          userData_store.schoolYearStartDate = entitlements ? entitlements.schoolYearStartDate : null;
          userData_store.sorConfig = (entitlements && entitlements.sorConfig) ? entitlements.sorConfig : defaultSORConfig;
          userData_store.resourceProviders = (entitlements && entitlements.resourceProviders) ? entitlements.resourceProviders : [];
          userData_store.saxonEnabled = userData_store.resourceProviders.includes("Saxon");
          userData_store.statePercentileCuts = entitlements ? entitlements.statePercentileCuts : null;
          userData_store.prereaderAudioEnabled = entitlements ? entitlements.prereaderAudioEnabled : null;
          userData_store.useLatestARM = entitlements ? entitlements.useLatestARM : false;
          userData_store.demoLockdown = entitlements ? entitlements.demoLockdown : false;
          userData_store.calibrationEnabled = entitlements ? entitlements.calibrationEnabled : false;
          userData_store.demoMode = entitlements ? entitlements.demoMode : false;
          userData_store.iesEnabled = entitlements ? entitlements.iesEnabled : false;
          userData_store.districtAdminBadgingEnabled = entitlements ? entitlements.districtAdminBadgingEnabled : false;
          userData_store.expandedEarlyReaderEnabled = entitlements ? entitlements.expandedEarlyReaderEnabled : false;
          userData_store.badgingEnabled = entitlements ? entitlements.badgingEnabled : false;
          userData_store.windowDisabledByGrade = entitlements.windowDisabledByGrade;
          userData_store.showBadNetworkResponse = entitlements ? entitlements.showBadNetworkResponse : false;
          userData_store.curriculum = entitlements ? entitlements.curriculum : null;
          userData_store.fourCutlineCategoriesEnabled = entitlements?.fourCutlineCategoriesEnabled || false;
          userData_store.tutorAssignmentEnabled = entitlements?.tutorAssignmentEnabled || false;
          userData_store.allowLetterNamingAndSoundsRescoring = entitlements.allowLetterNamingAndSoundsRescoring !== undefined &&
            entitlements.allowLetterNamingAndSoundsRescoring !== null ?
            entitlements.allowLetterNamingAndSoundsRescoring : false;
          userData_store.ignoreBrowserCompatibilityCheck = entitlements ? entitlements.ignoreBrowserCompatibilityCheck : false;
          userData_store.assessmentTimerMultiplier = entitlements ? entitlements.assessmentTimerMultiplier || "1.5" : "1.5";
          // Make sure HMH has the right demo settings
          if(entitlements && userData_store.userName === 'hmh.demo@parkside.com'){
            userData_store.curriculum = null;
            userData_store.partner = "HMH";
            userData_store.resourceProviders = ["IntoReading"]
            userData_store.sorConfig.selectedStandard = "COMMON_CORE";
          }
          return Promise.resolve(userData_store);
        });
      });
    }
  })
  .catch(async err => {
    console.log("Error in auth",JSON.stringify(err));
    if(retries){
      console.log("Retrying auth",retries);
      // Calculate exponential backoff with jitter: base * 2^(attempt) + random jitter up to 500ms
      const attempt = MAX_RETRIES - retries; // 0-based attempt number
      const exponentialBackoff = backoff * Math.pow(2, attempt);
      const jitter = Math.random() * 500; // Add up to 500ms random jitter
      const delay = exponentialBackoff + jitter;
      
      console.log(`Retrying saveUserData in ${delay}ms (attempt ${attempt + 1})`);
      
      // Wait for the calculated delay
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the saveUserData call
      return saveUserData(authData, retries-1, backoff);
    }else{
      throw err;
    }
  });
}

export function magicGradeToAmiraGrade(inMagicGrade){
  const gradeLookup = [
    {label: "Kindergarden", grade: 0},  //NOTE: Misspelling intentional - this is how magic returns it.
    {label: "Kindergarten", grade: 0},
    {label: "First grade", grade: 1},
    {label: "Second grade", grade: 2},
    {label: "Third grade", grade: 3},
    {label: "Fourth grade", grade: 4},
    {label: "Fifth grade", grade: 5},
    {label: "Sixth grade", grade: 6}
  ];

  let grade = gradeLookup.filter(lookup => {
    return lookup.label === inMagicGrade;
  });

  let returnGrade = grade.length ? grade[0].grade : 6;

  return returnGrade;
}

export function getStudentInfo(userName){
  const url = "https://api.getmagicbox.com/services//user/v1.0/"+userName;

  return fetch(url,{method: "GET"})
  .then((resp) => resp.json())
  .then(function(resp){
    let info = {
      districtId: resp.data.districtID,
      schoolId: resp.data.schoolId,
    };
    info.grade = magicGradeToAmiraGrade(resp.data.grades[0]);
    return Promise.resolve(info);
  })
  .catch((err) => {
      return Promise.reject(err);
  });
}

function getSessionID () {
  // add user data when
  return `Amira-Field-Trial:${Date.now()}`;
}

// returns a deepCopy of the given object
// this can help us guard against accidentally mutating the store
function deepCopy(obj){
  return JSON.parse(JSON.stringify(obj));
}

// return all possible stories - NOTE: This is ONLY used when GET_STORIES_LOCALLY is true
export function getStories(){
  if(RUN_METADATA_DUMP){
    STORIES.forEach(story => {
    if(story.type == assignmentTypes.TUTOR){
      story.chapters[0].phrases.map(phrase => prefetchWordMetadata(phrase));
    }
    });
  }
  if(GET_STORIES_LOCALLY){
    return Promise.resolve(STORIES);
  } else{
    return Promise.reject(new Error("Tried to get all stories in existance from the back end!"));
  }
}

// get a specific story by id
export function getStory(id){
  id  = formatGuidForSIS(id);
  if(!GET_STORIES_LOCALLY){
    // the locale param changes the content instruction language, NOT the content language itself.
    let bilingualLocale = getUserData().bilingualLocale; // leyendo makes it 'es-mx
    let studentLocale = getUserData().locale; // student's bilingual setting
    let instructionLocale = bilingualLocale || studentLocale // undefined or whatever student locale setting is
    return studentRecordService.getStory(id, instructionLocale).then((response) => {
      return Promise.resolve(addStoryAttributes(response));
    });
  }else{
    for(let i=0;i<STORIES.length;i++){
      if(STORIES[i].storyId === id || STORIES[i].storyid === id)
      {
        return Promise.resolve(STORIES[i]);
      }
    }
    //Turns out this only throws during development. Putting it in the proper error format anyway
    const notFoundError = new Error("Story Not Found",{storyId:id})
    return Promise.reject(notFoundError);
  }
}

function addStoryAttributes(story){
  //TODO: consider adding this on the backend instead
  story.locale = getUserData().locale;
  if(story.type == assignmentTypes.SPELLING){
    story.nonAudio = true;
  }

  if(story.tags && story.tags.length){
    story.brand = GetStoryBranding(story.tags);
  }

  return story;
}

//NOTE: This is ONLY used when GET_STORIES_LOCALLY is true
function getLocalAssessmentStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let recommended_stories = [];
    for(let i=0;i<inStories.length;i++){
      if((inStories[i].grade == inGrade) && (inStories[i].type == assignmentTypes.ASSESSMENT)){
        recommended_stories.push({
          title: inStories[i].title,
          author: inStories[i].author,
          id: inStories[i].storyId,
        });
        return recommended_stories;
      }
    }
  }
  else{
    throw new Error("Tried to call get assessment stories from back end");
  }
}

function getLocalSpellingStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let userData = getUserData();
    let locale = userData.locale;
    if(locale == "es-mx"){
      return [inStories[4]];
    } else {
      return [inStories[3]];
    }
  }else{
    throw new Error("Tried to call get local spelling stories instead of pulling from backend");
  }
}

function getLocalQuizStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    return [inStories[5]];
  }else{
    throw new Error("Tried to call get local spelling stories instead of pulling from backend");
  }
}

function getLocalNWFStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let userData = getUserData();
    let locale = userData.locale;
    if(locale == "es-mx"){
      return [inStories[2]];
    } else {
      return [inStories[1]];
    }
  }else{
    throw new Error("Tried to call get local NWF stories instead of pulling from backend");
  }
}

function getLocalDyslexiaStories(inStories,inGrade){
  //NOTE: we don't currently have a local spanish version of the dyslexia
  if(GET_STORIES_LOCALLY){
    return [inStories[3]];
  }else{
    throw new Error("Tried to call get local dyslexia stories instead of pulling from backend");
  }
}

function getLocalReadAloudStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    // return [inStories[66]]; //audio
    return [inStories[0]]; //video
  }else{
    throw new Error("Tried to call get local read aloud stories instead of pulling from backend");
  }
}

//NOTE: This is ONLY used when GET_STORIES_LOCALLY is true
function getLocalTutorStories(inStories,inGrade,inNumberOfStories){
  if(GET_STORIES_LOCALLY){
    let recommended_stories = [];
    for(let i=0;i<inStories.length;i++){
      if((inStories[i].grade == inGrade) && (inStories[i].type == assignmentTypes.TUTOR)){
        recommended_stories.push({
          title: inStories[i].title,
          author: inStories[i].author,
          id: inStories[i].storyId,
          storyid: inStories[i].storyid,
        });
        if(recommended_stories.length > inNumberOfStories){
          break;
        }
      }
    }
    return recommended_stories;
  }else{
    throw new Error("Tried to call get tutor stories from back end");
  }
}

function getLocalReadingComprehensionStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let readingCompStories = inStories.filter(s => s.type === assignmentTypes.READING_COMPREHENSION);
    return [readingCompStories[0]];
  }else{
    throw new Error("Tried to call get local reading comprehension stories instead of pulling from backend");
  }
}

function getLocalVocabularyScreenerStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let vocabScreenerStories = inStories.filter(s => s.type === assignmentTypes.VOCABULARY_SCREENER);
    return [vocabScreenerStories[0]];
  }else{
    throw new Error("Tried to call get local vocabulary screener stories instead of pulling from backend");
  }
}

// get the recommended stories for a particular user
// right now this just returns the first three stories from our stories store
// eventually it will take a studentId
export function getRecommendedStories(grade=1,type=assignmentTypes.TUTOR,storyID="",retries=2,sequenceMode,assignmentType,backoff=250, contentPackOverride, storiesLength, appType, filterOnGradeLimits){
  if (retries == null || retries == undefined) {
    retries = 2;
  }
  if(!GET_STORIES_LOCALLY){
    let userData = getUserData();

    let numStories = (type !== assignmentTypes.TUTOR) ? 6 : 5;
    if (storiesLength) numStories = storiesLength;
    let user = userData.userId;
    if((type==assignmentTypes.DYSLEXIA) && (userData.actualGrade || userData.actualGrade == 0)){
      grade = userData.actualGrade; //Always screen dyslexia students at their actual grade
    }

    if(type === assignmentTypes.ASSESSMENT && assignmentType === ASSIGNMENT_TYPE.PROGRESS_MONITORING){
      return getProgressMonitoringStory(grade,userData.userId,userData.spanishConfig)
      .then((story)=>{
        return Promise.resolve(story);
      })
      .catch(err => {
        logSentryException(new Error('Progress monitoring story fetch failure'), 'Error on getProgressMonitoringStory', { originalError: err?.message, originalStack: err?.stack });
        return Promise.reject(err);
      });
    }else{
      let formOverrides = getUserData().formOverrides;
      let screenerOverrides = getUserData().screenerOverrides;
      let overrideToUse = null;
      if (formOverrides[type]) {
        if (grade === -1) {
          overrideToUse = formOverrides[type][formOverrides[type].length - 1];
        } else {
          overrideToUse = formOverrides[type][grade];
        }
      }

      //If there is a screener override (mainly used with calibration), use that instead
      if(screenerOverrides && screenerOverrides[type]){
        overrideToUse = screenerOverrides[type];
      }

      if(overrideToUse && userData.locale !== 'es-mx')
      {
        return getStory(overrideToUse)
          .then(story=>{
            return Promise.resolve([story]);
          });
      }else{
        return studentRecordService.getRecommendedStories(user,grade,type,numStories,storyID,sequenceMode, userData.locale, contentPackOverride, appType, filterOnGradeLimits)
        .then((stories)=>{
          stories = stories.filter((story)=>{
            if(userData.locale !== "es-mx"){
              return (!story.tags || (story.tags && !story.tags.includes("ES-MX") && !story.tags.includes("es-mx")));
            }else{
              return(story.tags && (story.tags.includes("ES-MX") || story.tags.includes("es-mx")));
            }
          });

          return Promise.resolve(stories);
        })
        .catch(err=>{
          if(retries){
            console.log("ERROR IN RECOMMEND: RETRYING STORY RECOMMENDATION REQUEST", err);
            return new Promise(
              resolve => setTimeout(
                () => resolve(getRecommendedStories(grade,type,storyID,retries-1,sequenceMode, assignmentType, backoff+250, contentPackOverride, storiesLength, appType, filterOnGradeLimits)),
                backoff
            ));
          }else{
            console.log("ERROR IN RECOMMEND: STORY RECOMMENDATION RETRIES FAILED", err);
            return Promise.reject(err);
          }
        });
      }
    }
  }else{
    return getStories().then((stories) => {
      if(type == assignmentTypes.ASSESSMENT){
        return getLocalAssessmentStories(stories,grade);
      } else if(type == assignmentTypes.DYSLEXIA){
        return getLocalDyslexiaStories(stories,grade);
      } else if(type == assignmentTypes.NWF){
        return getLocalNWFStories(stories,grade);
      } else if(type == assignmentTypes.SPELLING){
        return getLocalSpellingStories(stories,grade); //TODO: use a switch instead here
      } else if(type == assignmentTypes.LISTENING_COMPREHENSION){
        return getLocalReadAloudStories(stories,grade);
      } else if(type == assignmentTypes.READING_COMPREHENSION){
        return getLocalReadingComprehensionStories(stories,grade);
      } else if(type == assignmentTypes.VOCABULARY_SCREENER){
        return getLocalVocabularyScreenerStories(stories,grade);
      } else {
        //Warning: If we switch back to getting stories from the student record service
        // before the recommendation service is implemented, need to guard against this
        // by checking GET_STORIES_LOCALLY.
        return getLocalTutorStories(stories,grade,3);
      }
    });
  }
}

const tutorWhitelist = [
  { 
    "storyid": "93405360-362f-11e9-bb3b-e7e2c0de6bee",
    "title": "The Life Cycle of Butterflies",
    "AREA_85": 8.6,
    "AREA_95": 10.2,
  },
  {
    "storyid": "d2669700-1ed8-11eb-83fe-57c4f9003880",
    "title": "Mustache Day",
    "AREA_85": 7.4,
    "AREA_95": 10.1,
  },
  {
    "title": "Animal Homes",
    "storyid": "73d63580-f9b0-11ea-8ba7-4f55b44592d0",
    "AREA_85": 6.8,
    "AREA_95": 8.7,
  },
  {
    "title": "FYI for kids: Bigger than an elephant",
    "storyid": "aa9a8ee0-f9c9-11ea-9b55-25ca75ea58d7",
    "AREA_85": 8.1,
    "AREA_95": 10.1,
  },
  {
    "title": "Bumblebees",
    "storyid": "f9ce3830-0974-11eb-8a99-238a1e6e21be",
    "AREA_85": 8,
    "AREA_95": 10.9,
  }
];

//Latches the mid-session ERSS outage report to one Sentry event per outage episode per session
//(cleared as soon as ERSS answers again). See the report site inside getStoriesByGrade. (TT-1420)
//Keyed by locale: an ESL student can switch between the en-us and es-mx Tutor options in
//StudentHome without a reload, so one shared flag would swallow the first es-mx report after an
//en-us one. (TT-1421)
const erssMidSessionOutageReportedLocales = new Set();

export function resetErssMidSessionOutageReportForTests(){
  erssMidSessionOutageReportedLocales.clear();
}

//TODO: this is a hook. Once we have access to stories by grade, fill this in
export async function getStoriesByGrade(grade,type=assignmentTypes.TUTOR,assignmentType,selectStories=false, skillId = undefined, sequenceMode = undefined, preventFallback = false, appType = undefined, filterOnGradeLimits = undefined){
  let storyID = "";

  const demoStories = [
    "19dcc770-74db-11e9-97ec-692434f2ed7f",
    "c12e53c0-1e70-11e9-ae42-8b4b4946542e",
    "1ede33a0-e658-11e9-b032-a3eb44035fc3",
    "93405360-362f-11e9-bb3b-e7e2c0de6bee",
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 4th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 5th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 6th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 7th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 8th Grade Story
    "19dcc770-74db-11e9-97ec-692434f2ed7f", //###NEED PreK Story
  ];

  const spanishDemoStories = [
    "0396de80-2abc-11ec-a861-0fbd689a9a10",
    "ab02f450-e809-11ec-a610-0ba5ccbff49d",
    "186718c0-8c1e-11ec-8b46-99c46be73253",
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 3rd Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 4th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 5th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 6th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 7th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 8th Grade Story
    "0396de80-2abc-11ec-a861-0fbd689a9a10", //###NEED PreK Story
  ];

  //These are the default tutor stories that a student will get if they haven't been assessed
  const tutorOverride = [
    "266a5ee0-db3f-11eb-9f17-b5ec7c0477f0",
    "654eb920-db40-11eb-9f17-b5ec7c0477f0",
    "1b8e30c0-db42-11eb-9f17-b5ec7c0477f0",
    "007da520-436b-11ee-9310-b1a78efda0ba",
    "d43ddcd0-203d-11eb-83fe-57c4f9003880",
    "95754380-0125-11eb-a111-7f2b0f4514dc",
    "95754380-0125-11eb-a111-7f2b0f4514dc",
    "95754380-0125-11eb-a111-7f2b0f4514dc",
    "95754380-0125-11eb-a111-7f2b0f4514dc",
    "266a5ee0-db3f-11eb-9f17-b5ec7c0477f0",
  ];

  const spanishTutorOverride = [
    "0396de80-2abc-11ec-a861-0fbd689a9a10",
    "10a6e750-1004-11ec-8b5b-a77cd59fd6b9",
    "33eda100-143b-11ec-a8ee-679889a2bda0",
    "e20be2c0-219b-11ec-b074-6727926623c1",
    "fdbc4230-1679-11ec-9a76-6727f7be53e0",
    "bb2d0060-2094-11ec-a3c7-33c4d99154fa",
    "bb2d0060-2094-11ec-a3c7-33c4d99154fa",
    "bb2d0060-2094-11ec-a3c7-33c4d99154fa",
    "bb2d0060-2094-11ec-a3c7-33c4d99154fa",
    "0396de80-2abc-11ec-a861-0fbd689a9a10",
  ];

  const SPANISH_PILOT_STORIES_BY_GRADE = {
    1: [
      'fe3549b0-2d42-11ed-a30a-699ae3cc1f26',
      '4b06d200-3f5d-11ed-bdcc-a9c8c89d84cc',
      'c37dfaf0-19f5-11ef-8449-c55dffd98e48',
    ],
    4: [
      '76136940-b307-11ed-9741-01e8b90bff6d',
      'be9b07e0-9e8a-11ed-ab7e-474a736a5b26',
      '66faa3b0-b2da-11ed-9741-01e8b90bff6d',
    ],
  };

  // PILOT ONLY: Spanish pilot story pool — requires isSpanishPilot=true URL param
  // This is the only remaining use of isSpanishPilot for story selection; other pilot features
  // have been transitioned to the default Spanish experience (see ASSMNT-1126)
  if (_isSpanishPilot) {
    const pilotGrade = SPANISH_PILOT_STORIES_BY_GRADE[grade] ? grade : 1;
    const storyIds = SPANISH_PILOT_STORIES_BY_GRADE[pilotGrade];
    const stories = await Promise.all(storyIds.map(id => getStory(id)));
    return stories;
  }

  if (skillId) { // if skillId is passed in, call getRecommendedStories with the given skillId and return
    try {
      const skillStories = await getRecommendedStories(grade, assignmentTypes.TUTOR, "", 2, sequenceMode || "NO_SEQUENCE", assignmentTypes.TUTOR, 250, {
        contentPackId: `SKILL-${skillId}`, mode: 'STRICT'
      }, 1, appType, filterOnGradeLimits);

      // If skillTutor storyRecService returns stories, use them
      if (skillStories && skillStories.length > 0) {
        return skillStories;
      } else {
        logSessionInfo('no_stories_returned_from_skillTutor', { error: 'No stories returned from skillTutor', retriesRemaining: 1 });
      }

      if (preventFallback) {
        return [];
      }

      // If no stories returned, retry without skillId parameter (limit of 1)
      const fallbackStories = await getRecommendedStories(grade, assignmentTypes.TUTOR, "", 2, "NO_SEQUENCE", assignmentTypes.TUTOR, 250, null, 1, appType, filterOnGradeLimits);
      return fallbackStories;
    } catch (error) {
      console.error(`Error getting stories for skillId ${skillId}:`, error);
      logSessionInfo('error_in_recommend', { error: error.message, retriesRemaining: 1 });

      if (preventFallback) {
        return [];
      }

      // If there's an error, retry without skillId parameter
      console.log(`Retrying without skillId parameter due to error`);
      const fallbackStories = await getRecommendedStories(grade, assignmentTypes.TUTOR, "", 2, "NO_SEQUENCE", assignmentTypes.TUTOR, 250, null, 1, appType, filterOnGradeLimits);
      return fallbackStories;
    }
  }

  if(grade > demoStories.length - 2) grade = demoStories.length - 2; // Accounts for last index being for PREK
  let sequenceStart = getUserData().sequenceStartStories.find(element => element.locale === getUserData().locale);

  if((type == assignmentTypes.TUTOR) && (selectStories || getUserData().demoMode)){
    let demoTable = getUserData().locale === 'es-mx' ? spanishDemoStories : demoStories;
    storyID = grade === -1 ? demoTable[demoTable.length - 1] : demoTable[grade];
    return getStory(storyID)
    .then(story=>{
      return Promise.resolve([story]);
    });
  }else{
    if((type == assignmentTypes.TUTOR) && (getUserData().forceTutor) && !GET_STORIES_LOCALLY && !sequenceStart){
      storyID = getUserData().locale == 'en-us' ?
        (grade === -1 ? tutorOverride[tutorOverride.length - 1] : tutorOverride[grade]) :
        (grade === -1 ? spanishTutorOverride[spanishTutorOverride.length - 1] : spanishTutorOverride[grade]);
      userData_store.forceTutor = false; //Make sure if they complete the story they can move on.
      return getStory(storyID)
      .then(story=>{
        return Promise.resolve([story]);
      });
    }else if(type == assignmentTypes.TUTOR && (getUserData().isEarlyReader || getUserData().isSpanishEarlyReader || getUserData().isSaxonReader)){
      const hasSequenceStart = getUserData().sequenceStartStories.filter((story)=>story.locale === getUserData().locale).length > 0;
      if(getUserData().startingEarlyReaderSequence && hasSequenceStart){
        if(sequenceStart){
          storyID = sequenceStart.storyId;
          // This makes sure that the user doesn't continually get forced into the first story if they keep taking stories in this session
          let dropSequence = getUserData().sequenceStartStories.filter((story)=>story.storyId !==sequenceStart.storyId);
          setUserData("sequenceStartStories",dropSequence);
        }
      }else{
        // English and Spanish early readers take the next story from ERSS's own sequence state.
        // Saxon still uses the recommender's locked sequence.
        const userData = getUserData();
        let nextStory;
        if(!userData.isSaxonReader && userData.locale === 'en-us' && userData.isEarlyReader){
          nextStory = await getNextEarlyReaderStory(userData.userId,true,false);
        } else if(!userData.isSaxonReader && userData.locale === 'es-mx' && userData.isSpanishEarlyReader){
          nextStory = await getNextEarlyReaderStory(userData.userId,false,true);
        }
        if(nextStory !== undefined){
          // null means ERSS never answered (see getNextEarlyReaderStory). Deliberately NOT fatal:
          // a null anchor into SEQUENCE_LOCKED is the same thing an early reader who just finished
          // the sequence sends ([{locale, storyId: null}]), so SRS still recommends a story and the
          // student reads off-sequence content instead of hitting FATAL_REC_FETCH_FAILURE. Before
          // TT-1420 this path threw and blocked them. What we lose by degrading is the ability to
          // tell an outage apart from sequence completion, so report it explicitly. (TT-1420)
          //
          //Sentry only: getNextEarlyReaderStory already writes the erss_unavailable session log
          //with the reason, and this runs on every return to the story picker, so a second session
          //log per occurrence would just double the volume of an outage that is already recorded.
          //
          //Once per outage episode, not once per picker visit. An ERSS outage is fleet-wide, so an
          //unbounded report would spend Sentry quota during the incident it exists to surface, and
          //logSentryException lays down two scope breadcrumbs per call — at the v7 default of 100,
          //~50 returns evict the whole trail and an unrelated crash later in the session arrives
          //with no context but this message. The latch clears on recovery so a second outage in the
          //same session still reports. Per-occurrence detail stays in the erss_unavailable log.
          //
          //studentId/grade/locale go in hintContext as tags/extra, not just the breadcrumb data, so
          //the event is groupable in Sentry — "how many students, which grades, which locale" is the
          //question an outage actually raises. actualGrade, because `grade` has been clamped above
          //by the demoStories bound (and -1 is PreK).
          if(nextStory === null){
            if(!erssMidSessionOutageReportedLocales.has(userData.locale)){
              erssMidSessionOutageReportedLocales.add(userData.locale);
              const outageContext = { studentId: userData.userId, actualGrade: userData.actualGrade, locale: userData.locale };
              logSentryException(
                new Error('ERSS unavailable mid-session'),
                'getNextEarlyReaderStory returned null; falling back to an unanchored SEQUENCE_LOCKED recommendation',
                outageContext,
                { tags: { erssOutage: 'mid_session' }, extra: outageContext },
              );
            }
          }else{
            erssMidSessionOutageReportedLocales.delete(userData.locale);
          }
          //By locale, not [0]: ERSS answers one entry per requested locale, and the login path
          //(isEarlyReaderInSequence) already looks the entry up the same way. [0] would quietly
          //anchor a Spanish session on an English story if both locales ever came back.
          storyID = nextStory?.find((s) => s.locale === userData.locale)?.storyId ?? null;
        }
      }
      userData_store.forceTutor = false; //Make sure that if this is a new user then they don't get forced into the opening story in this session
      return await getRecommendedStories(grade,type,storyID,null,recommendationModes.SEQUENCE_LOCKED,null,250,null,null,appType,filterOnGradeLimits);
    }else{
      if(type == assignmentTypes.DYSLEXIA){
        return getDyslexiaStories(type,grade);
      }else if(type === assignmentTypes.READING_COMPREHENSION){
        const readingCompStories = await getRecommendedStories(grade,type,null,null,null,null,250,null,null,appType,filterOnGradeLimits);
        const currentScreeningWindow = getScreeningWindow();
        return readingCompStories;
        //###Temporarily disable this: return [readingCompStories.find(s => s.tags && s.tags.length && s.tags.includes(currentScreeningWindow))];
      }else {
        if(type == assignmentTypes.TUTOR && getUserData().tutorWhitelist){
          return recommendFromWhitelist();
        }else{
          return getRecommendedStories(grade,type,storyID,null,null,assignmentType,250,null,null,appType,filterOnGradeLimits);
        }
      }
    }
  }
}

async function recommendFromWhitelist(whiteList = tutorWhitelist){
  let takenActivities = await studentRecordService.checkForCompletedActivities(getUserData().userId);

  let availableStories = whiteList.filter((story)=>{
    return (takenActivities.filter((activity)=>{
      return ((activity.storyId === uuidToHEX(story.storyid)) && (activity.status == "scored" || activity.status == "rescored" || activity.displayStatus == "completed"))
    }).length === 0);
  });

  if(availableStories.length){
    //Recommend the story that has the closest to the midpoint between AREA_85 and AREA_95
    let difficulties = availableStories.map((story)=>{
      let diff = (story.AREA_95 - story.AREA_85)/2 + story.AREA_85;
      return Math.abs(getUserData().AREA - diff);
    })
    let indexOfSmallest = difficulties.indexOf(Math.min.apply(Math, difficulties));
    console.log("RECOMMEND",getUserData().AREA,availableStories[indexOfSmallest]);
    return studentRecordService.getStory(availableStories[indexOfSmallest].storyid)
    .then((story)=>{return Promise.resolve([story])});
  }else{
    return [];
  }

}

function getDyslexiaStories(type,grade){
  //NOTE: We don't have alternate letter name forms. Leave this in as a placeholder until and unless we re-enable this.
  const dyslexiaOverride = [
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null
  ];

  let formOverrides = getUserData().formOverrides;
  let locale = getUserData().locale;

  //If this is a calibration assignment, override everything (including formOverrides) with that;
  const {screenerOverrides} = getUserData();
  let calibrationOverride = screenerOverrides && screenerOverrides.dyslexia;
  let formToUse = calibrationOverride ? calibrationOverride : formOverrides[assignmentTypes.DYSLEXIA] && (
    grade === -1 ?
      formOverrides[assignmentTypes.DYSLEXIA][formOverrides[assignmentTypes.DYSLEXIA].length - 1] :
      formOverrides[assignmentTypes.DYSLEXIA][grade]
  );

  //No form override for spanish (yet)
  if(!(locale === "es-mx") && formToUse){
    return getStory(formToUse)
      .then(story=>{
        return Promise.resolve([story]);
      });
  }else{
    //If the letter names are configured and we have a letter name form, grab it. Otherwise just grab the "normal" test.
    const dyslexiaOverrideVal = grade === -1 ? dyslexiaOverride[dyslexiaOverride.length - 1] : dyslexiaOverride[grade];
    const letterNameConfigVal = grade === -1 ? getUserData().letternameConfig[getUserData().letternameConfig.length - 1] : getUserData().letternameConfig[grade];
    if(!(locale === "es-mx") && (type == assignmentTypes.DYSLEXIA) && dyslexiaOverrideVal && letterNameConfigVal){
      return getStory(dyslexiaOverrideVal)
      .then(story=>{
        return Promise.resolve([story]);
      })
    }else{
      return getRecommendedStories(grade,type);
    }
  }
}

// gets a story by id and then paginates it for use in the tutor app
export function getPaginatedStory(id, phrasesPerPage){
  return getStory(id).then((story) => {
    return paginateStory(story, getPhrasesPerPage(story) || phrasesPerPage);
  });
}

// gets a story by id and then squashes it for use in assessment
export function getSquashedStory(id){
  return getStory(id).then((story) => {
    return paginateStory(squashStory(story));
  });
}

export function processAssessmentStory(rawStory) {
  let story = addStoryAttributes(rawStory);
  let wordsPerPhrase = 50;
  if (story.type !== assignmentTypes.ASSESSMENT) {
    wordsPerPhrase = null;
  }
  return paginateStory(story, 1, wordsPerPhrase);
}

// gets a story paginated at 1 phrase per page, combining phrases as necessary to make long enough pages for assessment mode
export function getAssessmentStory(id) {
  return getStory(id).then((rawStory) => {
    return processAssessmentStory(rawStory);
  });
}

// divides story into a given number (default 3) of phrase pages
export function paginateStory(story, phrasesPerPage=3, wordsPerPhrase=null){
  let pages = [];
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    let currentPhrase = "";
    let currentPage = [];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];

      //Map logical item types to display types
      if(chapter.items && chapter.items[j]){
        chapter.items[j].type = getDisplayType(chapter.items[j].type);
        if(chapter.items[j].instructions && chapter.items[j].instructions.type){
          chapter.items[j].instructions.type = getDisplayType(chapter.items[j].instructions.type);
        }
      }

      if(!wordsPerPhrase || currentPhrase.split(" ").length + phrase.split(" ").length > wordsPerPhrase){
        if(currentPhrase !== ""){
          currentPage.push(currentPhrase);
        }
        currentPhrase = phrase;
      } else {
        currentPhrase = currentPhrase + " " + phrase;
      }

      // list of item types that should be one phrase per page
      const typesWithOnePhrasePerPage = [phraseTypes.NONSENSE_WORD_REPETITION,
        phraseTypes.CHANGE_ONE_PART,
        phraseTypes.WORD_PART_DELETION,
        phraseTypes.UP_AND_DOWN,
        phraseTypes.SPELLING,
      ];

      if((currentPhrase === "") && typesWithOnePhrasePerPage.includes(get(chapter, "items["+j+"].type"))){
        currentPage.push(currentPhrase);
        pages.push(currentPage);
        currentPhrase="";
        currentPage = [];
      }else{
        if(currentPage.length >= phrasesPerPage){
          pages.push(currentPage);
          currentPage = [];
        }

        // if this is the last phrase add the current phrase and page we are working on even if they are not long enough yet
        if(j >= chapter.phrases.length - 1){
          currentPage.push(currentPhrase);
          pages.push(currentPage);
        }
      }
    }
  }
  return {
    title: story.title,
    author: story.author,
    alternateIntroText: story.alternateIntroText,
    pages: pages,
    items: story.chapters[0].items,
    phrases: story.chapters[0].phrases,
    questions: story.questions,
    storyId: story.storyId,
    type: story.type,
    version: story.version,
    grade: story.grade,
    nonAudio: story.nonAudio,
    // Dropping this resolves the Formative idle-timeout override in mock mode only: AOS serves
    // a Formative testlet under the task type its blueprint asks for, so story.type is never
    // 'formative' on real data and getIdleTimeoutsForStory has nothing else to key off.
    isFormative: story.isFormative,
    brand: story.brand,
    tags: story.tags,
    maxGradeLevel: story.maxGradeLevel,
    levelingDelta: story.levelingDelta,
    metadataOverrides: story.chapters[0].overrides,
    referenceStoryId: story.referenceStoryId,
    referenceStoryPhrases: story.referenceStoryPhrases,
  };
}

  //This method maps the abstract types of an item to our display types
function getDisplayType(type){
    switch(type){
      case 'blending':
        return "video";
      case 'wordList':
        return "word";
      case 'letterSound':
        return "sound";
      case 'beginningLetterSound':
      case 'BeginningSound':
        return "sound";
      case 'ran':
        return "RAN";
      case 'spelling':
        return "textInput";
      case 'quiz':
        return 'quiz';
      case 'readAloudVideo':
        return 'readAloudVideo';
      default:
        return type;
    }
}

// returns a story with all phrases concatinated into one single phrase
function squashStory(story){
  let metaPhrase = "";
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];
      if(!(i == 0 && j == 0)){
        metaPhrase += " ";
      }
      metaPhrase += phrase;
    }
  }
  return({
    title: story.title,
    author: story.author,
    storyId: story.storyId,
    version: story.version,
    type: story.type,
    storyTags: story.tags,
    chapters: [
      {phrases: [metaPhrase]}
    ],
  });
}


//TODO: this should not be necessary when we are getting back scored story data from the back end
// unwrap phrases with possibility of adding meta data. originally was considering keeping errors,
// mispronunciations etc in here, but since we are mutating them parallel arrays makes it easier for now
function unwrapPhrases(activity){
  let story = activity.story;
  let errors = activity.errors;
  let phrases = [];
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];
      let phraseErrors = errors ? errors[j] : [];   //relying on the fact that we are not actually subdividing by chapter, and that the errors array is not currently indexed by chapter
      let markedPhrase = {
        text: phrase,
      }
      phrases.push(markedPhrase);
    }
  }
  return phrases;
}

//returns an object containing a paginated story, an audio file of a student reading it, and amira's guess at errors
// to be used in teacher correction UI
export function getScoredStory(activityId){
  return studentRecordService.getActivity(activityId).then((response) => {
    let activity = response.data.getActivity[0];
    //Handle empty error elements
    let phrases = activity.story.chapters[0].phrases;
    let errors = activity.errors;
    for(let i=0;i<errors.length;i++){
      if(errors[i].length === 0){
        errors[i] = new Array(phrases[i].split(' ').length).fill(true);
      }
    }

    //TODO: this really should not be three chained calls on this end, this info needs to be consolidated on the backend

    return ({
      ...activity,
      squashedStory: squashStory(activity.story),
      audio: null,                              //activity.audio,  TODO: link this on the backend
      mispronunciations: activity.mispronunciations || [],
      skips: activity.skips || [],
      selfCorrections: activity.selfCorrections || [],
      phrases: unwrapPhrases(activity),
      sidebarStatus: getActivityStatus(activity,false),
      updatedAt: new Date(activity.updatedAt),
      flaggedPhrases: activity.flagging,
      wcpmScore: activity.scores.wcpmScore,
    });
  }).then((scoredStory) => {
    // after we get back the story, populate the audio field manualy. Eventually this will be linked on the back end
    return searchSessionAudio(activityId).then((sessionAudio) => {
      scoredStory.audio = {url: sessionAudio};
      return scoredStory;
    });
  }).then((scoredStory) => {
    return studentInformationService.getUserInfoByUserId(scoredStory.studentId).then((studentData) => {
      if(!studentData){
        return scoredStory;
      }

      let student = {
        studentId: scoredStory.studentId,
        firstName: studentData.FirstName,
        lastName: studentData.LastName,
        userName: studentData.UserName,
      }
      scoredStory.student = student;
      return scoredStory;
    });
  });
}

// Not exported: every caller goes through hasAnyCompletedActivity so the session cache cannot be
// bypassed by accident.
async function checkForAnyCompletedActivity(studentId){
  let returnValue = false;
  let completed = await studentRecordService.checkForCompletedActivities(studentId);

  completed.map((activity)=>{
    // We are going to consider a previous activity completed if it is tutor or assessment AND:
    // 1. It is scored or rescored OR
    // 2. It is a tutor and has been completed, but not necessarily scored OR
    // 3. The student has been classified as a prereader
    if(((activity.type === assignmentTypes.ASSESSMENT) || (activity.type === assignmentTypes.TUTOR))&&
      ((activity.status === statusType.SCORED) || (activity.status === statusType.RESCORED) ||
       ((activity.type === assignmentTypes.TUTOR) && (activity.displayStatus === statusType.COMPLETED)) ||
       (activity.displayStatus == statusType.PREREADER))){
        returnValue = true;
    }
  });

  return Promise.resolve(returnValue);
}

/**
 * Login-time replacement for the checkForAnyCompletedActivity read in App.checkAssignments and
 * App.handleStudentInboxTutor. saveUserData has already derived a true answer from the rows it
 * fetched whenever they held one (userData.anyCompletedActivity), so the 30-row GQL_CHECK_SCORED
 * read only runs for students with no completed activity on hand — the population the old code
 * read for every time. A true answer is final for the session and is written back, so the refresh
 * after a completed story asks nothing.
 */
export async function hasAnyCompletedActivity(studentId) {
  // A session cache, not a per-student one: userData_store holds exactly the logged-in student
  // and clearUserData resets it on logout, so the cached answer is that student's by construction.
  if (userData_store.anyCompletedActivity === true) {
    return true;
  }
  const completed = await checkForAnyCompletedActivity(studentId);
  if (completed === true) {
    userData_store.anyCompletedActivity = true;
  }
  return completed;
}

async function findAssignments(inActivities,studentId){
  let completedAssessment = false;
  let assignmentActivity = null;

  inActivities.map((activity)=>{
    //Check for an assignment, if we have one, we're essentially done.
    if(activity.status === activityStatus.ASSIGNED){
      if(!assignmentActivity){
        assignmentActivity = activity;
      }else{
        //Remove any duplicate assignments
        studentRecordService.markCompleted(activity.activityId,statusType.UNASSIGNED)
          .catch((error) => {
            console.error('Error unassigning duplicate activity:', error);
            logSessionInfo('error_unassigning_duplicate', {
              error: error?.message || error,
              activityId: activity.activityId
            });
          });
        logSessionInfo("unassigned_duplicate",activity);
      }
    }

    // We are going to consider a previous assessment completed if:
    // 1. It is scored or rescored (duh)
    // 2. It has been completed (this covers us with stuck or status of scoring)
    // 3. The student has been classified as a prereader
    if(activity.type === assignmentTypes.ASSESSMENT && inCurrentScreeningWindow(new Date(activity.createdAt)) &&
      ((activity.status === statusType.SCORED) || (activity.status === statusType.RESCORED) ||
       (activity.displayStatus === statusType.COMPLETED) || (activity.displayStatus == statusType.PREREADER))){
        if(activity.story.tags && activity.story.tags.includes("BENCHMARK")){
          completedAssessment = true;
        }
    }
  });

  //If there is neither a completed assignment nor an assigned activity, create one
  //TODO: remove benchmark window condition
  //TODO: continue support for autoAssign license?
  if(!completedAssessment && !assignmentActivity && (getUserData().autoAssign || inBenchmarkScreeningWindow())){
    return studentRecordService.createActivity("assessment", studentId, "1234", "assigned","not_started",getUserData().districtId,getUserData().schoolId).then((response) => {
      if(response.data && response.data.putActivity && response.data.putActivity.activityId){
        return Promise.resolve(response.data.putActivity);
      }
    }).catch((error) => {
      console.error("Failed to create auto-assignment activity", error);
      logSessionInfo("put_activity_auto_assignment_failure", {
        studentId,
        error: error.message
      });
      // Return null so the caller can handle the absence of an assignment
      return Promise.resolve(null);
    });
  }else{
    return Promise.resolve(assignmentActivity);
  }
}

// return average WCPM and accuracy and total points accrued for this student.
// If an activityId is passed in, we will make every attempt to get the current metrics from the backend
// by polling the activity for up to 5 seconds in case it hasn't scored yet. In the event that we don't get a score,
// the "current" versions of the metrics will be undefined.
// A locale ('es-mx' or the locales enum form) restricts the count to that locale's activities; callers that
// want every locale counted - the historical behavior - leave it out.
export async function getStudentMetrics(currentActivityId=null, equated=false, locale=null){
  return getValidActivitiesForStudents([{id: getUserData().userId}], 30, ["assessment","tutor"], equated, formatLocale_UpperCaseHyphen(locale)).then(async activities => {
    let stats = {
      avgWPM: 0,
      avgAccuracy: 0,
      totalPointsAccrued: 0,
      testsTaken: 0,
      storiesRead: 0,
    };
    let logStatement = null;

    activities.forEach(activity => {
      stats.avgWPM += activity.scores.wcpmScore ? Math.round(activity.scores.wcpmScore) : 0;
       let thisAccuracy = activity.errors ? calculateAccuracy(getNonFlaggedErrors([activity.errors],activity.flagging)) : 0;
       stats.avgAccuracy += thisAccuracy;
       stats.totalPointsAccrued += activity.points || 0;
      stats.testsTaken += (activity.type === assignmentTypes.ASSESSMENT) ? 1 : 0;
      stats.storiesRead += (activity.type === assignmentTypes.TUTOR) ? 1 : 0;

      //If the activity passed in has already scored (which it will be if it's in the results of the call above)
      // then use it for the current stats
      if(activity.activityId === currentActivityId){
        stats.currentWCPM = Math.round(activity.scores.wcpmScore);
        stats.currentAccuracy = thisAccuracy;
        if (activity.scoreEstimates) {
          stats.currentARMScore = activity.scoreEstimates.ARM_estimate;
        }
        logStatement = 'metrics_activity_was_scored';
      }
    });

    //If the activity hasn't scored yet, poll for 5 seconds until it's scored
    if(!stats.currentWCPM && currentActivityId){
      let thisActivity = await studentRecordService.waitUntilScored(currentActivityId,5000);
      if(thisActivity){
        stats.currentWCPM = Math.round(equated ? thisActivity.scores.equatedWCPM : thisActivity.scores.wcpmScore);
        stats.currentAccuracy = calculateAccuracy(getNonFlaggedErrors([thisActivity.errors],thisActivity.flagging));
        if (thisActivity.scoreEstimates) {
          stats.currentARMScore = thisActivity.scoreEstimates.ARM_estimate;
        }
        logStatement = 'metrics_activity_eventually_scored';
      }
    }
    stats.avgWPM = activities.length ? Math.round(stats.avgWPM / activities.length) : 0;
    stats.avgAccuracy = activities.length ? Math.round(stats.avgAccuracy / activities.length) : 0;

    if(logStatement) logSessionInfo(logStatement,stats);

    return stats;
  }).catch(err => {
    console.log("error in getStudentMetrics: ", err);
    return {
      avgWPM: 0,
      avgAccuracy: 0,
      totalPointsAccrued: 0,
    };
  });
}

//stub to save teacher corrections to scoring
export function saveScoredAssessment(activityId, errors, skips, selfCorrections, mispronunciations, timeRead, flat = true){
  let formattedErrors = errors ? errors : [[]]; // TODO modify backend so that this is not necessary

  if(flat){
    formattedErrors = [errors.flat(4)];
  }

  //Remove any empty error elements (so we don't implicitly pad)
  formattedErrors = formattedErrors.filter(function (el) {
    return el.length > 0;
  });

  return studentRecordService.scoreActivity(activityId, formattedErrors, skips, selfCorrections, mispronunciations, timeRead);
}

export function getRhymingWord(inWord,callback)
{
  //Get a transcription
  let apiName = 'lexaSpeech';
  let path = '/RhymesWith';
  let myInit = { // OPTIONAL
    headers: {  'Content-Type': 'application/json'
    },
    body: {wordToRhyme: inWord},
    response: false // OPTIONAL (return entire response object instead of response.data)
  }

  //console.log('Sending...')
  API.post(apiName, path, myInit)
    .then(resp => {
        callback(resp);
    })
    .catch(err => {
      console.log('API call to Rhyme failed: ',err);
      return(err);
    })

}

function getUtteranceStoreKey(text, includeSpeechMarks, locale){
  return text.replace(/\s/g, '') + "_" + includeSpeechMarks + "_" + locale;
}

//can be called by itself to preload an utterance, just throw away the return value
export async function getUtterance(text, includeSpeechMarks=false, locale="en-us", timeout=10000, useGenerative=false, previousText){
  if(!text) return Promise.reject(new Error("no utterance provided",{text}));
  // Spanish generative TTS: USE_NEW_SPANISH_VOICE + brand allow-list, or Spanish pilot URL param; English uses generative when flag is set
  const engine = ((useGenerative && isEnglishLocale(locale)) || shouldUseNewSpanishVoice(locale)) ? TTS_ENGINE_TYPE.generative : TTS_ENGINE_TYPE.standard;
  const key = getUtteranceStoreKey(text, includeSpeechMarks, locale);
  if(utterance_store.hasOwnProperty(key) && text){
    // if we've already stored this utterance, pull it from the cache
    // make sure to make it thenable
    return new Promise((resolve, reject) => {
      const cachedUtterance = utterance_store[key];
      // Check if blob URL is still valid by attempting to fetch it
      // If the blob URL is invalid (e.g., revoked or garbage collected), regenerate it
      validateBlobURL(cachedUtterance.audioURL).then(isValid => {
        let returnValue;
        if (!isValid && cachedUtterance.blob) {
          // Blob URL is invalid but we have the blob - regenerate the URL
          console.log('Regenerating invalid blob URL for cached utterance');
          // Revoke the old invalid URL (safe to call even if already invalid)
          try {
            URL.revokeObjectURL(cachedUtterance.audioURL);
          } catch (e) {
            // Ignore errors when revoking invalid URLs
          }
          // Create a new blob URL from the stored blob
          const newAudioURL = URL.createObjectURL(cachedUtterance.blob);
          
          // Update the cached utterance with the new URL
          utterance_store[key] = {
            ...cachedUtterance,
            audioURL: newAudioURL
          };
          
          returnValue = deepCopy(utterance_store[key]);
        } else if (!isValid && !cachedUtterance.blob) {
          // Blob URL is invalid and we don't have the blob - need to re-fetch
          console.warn('Cached utterance has invalid blob URL and no blob reference, removing from cache');
          delete utterance_store[key]; // delete the cached utterance for this key to avoid infinite recursion

          // call getUtterance again to re-fetch the utterance
          getUtterance(text, includeSpeechMarks, locale, timeout, useGenerative, previousText).then((result) => {
            resolve(result);
          }).catch(error => {
            reject(error);
          });
          return;
        } else {
          // Blob URL is valid, use cached utterance
          returnValue = deepCopy(cachedUtterance);
        }
        
        returnValue.id = new Date().getTime();
        returnValue.blob = cachedUtterance?.blob; // deepCopy will remove the blob, so we need to add it back
        resolve(returnValue);
      }).catch(error => {
        // If validation fails, fall back to using cached utterance
        console.warn('Blob URL validation failed, using cached utterance anyway:', error);
        let returnValue = deepCopy(cachedUtterance);
        returnValue.blob = cachedUtterance?.blob; // deepCopy will remove the blob, so we need to add it back
        returnValue.id = new Date().getTime();
        resolve(returnValue);
      });
    }); //copy the utterance before returning to eliminate any chance of mutating the store
  } else {
    // Check if there's already an in-flight request for this key
    // If so, return the existing promise to deduplicate concurrent calls
    if (utterance_inflight.hasOwnProperty(key)) {
      return utterance_inflight[key];
    }

    //Invoke the text to speech API
    if(!text.includes("<speak>")){
      text = '<speak>'+text+'</speak>';
    }
    let args = {
      textToSpeak: text,
      includeSpeechMarks: includeSpeechMarks,
      locale: locale,
      engine,
    }
    if(previousText){
      args.previousText = previousText;
    }

    //TODO: Think about this timeout and make sure we have retries.
    const requestPromise = withTimeout(getTextToSpeech(args), timeout, `Text-to-speech request timed out after ${timeout}ms`)
    .then(async resp => {
      //Build the audio buffer
      let uInt8Array = new Uint8Array(resp.speech_audio.AudioStream.data);
      let arrayBuffer = uInt8Array.buffer;
      let blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      let audioURL = URL.createObjectURL(blob);

      let utterance = {
        speech_marks: resp.speech_marks,
        speech_audio: resp.speech_audio.AudioStream.data,
        audioURL: audioURL,
        blob,
        id: new Date().getTime(),
        text: resp.text
      };

      //cache the utterance
      utterance_store[key] = utterance;
      const returnValue = deepCopy(utterance);
      returnValue.blob = utterance.blob; // deepCopy will remove the blob, so we need to add it back
      return Promise.resolve(returnValue);
    })
    .catch(err => {return Promise.reject(err)})
    .finally(() => {
      // Clean up in-flight tracking when done (success or failure)
      delete utterance_inflight[key];
    });

    // Store the promise so concurrent calls can reuse it
    utterance_inflight[key] = requestPromise;
    return requestPromise;
  }
}

function handleVoiceOverride(locale){
  const override = getUserData().voiceOverrides;
  if(!override || override.length === 0){
    return locale;
  }else{
    const newLocale = override.find(o=>o.locale === locale);
    return newLocale ? newLocale.override : locale;
  }
}

export async function getTextToSpeech(args,retries=4) {
  let inText = args.textToSpeak;

  inText = inText.replace(/[“”]/g, '"');

  let returnSpeechMarks = args.includeSpeechMarks;
  let locale = handleVoiceOverride(args.locale);
  let engine = args.engine;

  if (engine === TTS_ENGINE_TYPE.generative) {
    // Generative voice does not support prosody tags, emphasis tags, opening break tags, opening ellipsis, or spell-out tags
    inText = removeProsodyTags(inText);
    inText = removeEmphasisTags(inText);
    inText = removeOpeningBreakTag(inText);
    inText = removeOpeningEllipsis(inText);
    inText = parseTextWithSpellOutTag(inText);
  }

  const shouldSendEnglishSingleWordPreviousText = engine === TTS_ENGINE_TYPE.generative && isEnglishLocale(locale) && isSingleSpokenWord(inText);
  const previousText = args.previousText || (shouldSendEnglishSingleWordPreviousText ? ENGLISH_SINGLE_WORD_PREVIOUS_TEXT : undefined);
  const body = {inText, locale, engine};
  if(previousText){
    body.previous_text = previousText;
  }

  //Get a transcription
  let apiName = 'lexaSpeech';
  let path = '/TextToSpeech';
  let myInit = { // OPTIONAL
    headers: {  'Content-Type': 'application/json'
    },
    body,
    returnSpeechMarks,
    locale: locale,
    response: false // OPTIONAL (return entire response object instead of response.data)
  };

  const utteranceLogParams = {
    text: inText,
    locale,
    includeSpeechMarks: returnSpeechMarks,
    engine
  }

  storeUtteranceLog(utteranceLogParams);
  console.log("TTS API:", utteranceLogParams);

  return API.post(apiName, path, myInit)
  .then(resp => {
    resp.text = inText;
    if (
      !resp.speech_marks ||
      resp.speech_marks.length === 0 ||
      !resp.speech_marks[0].words ||
      resp.speech_marks[0].words.length === 0
    ) {
      // Generate visemes from text and audio buffer length
      if (resp.speech_audio && resp.speech_audio.AudioStream && resp.speech_audio.AudioStream.data) {
        const audioBufferLength = resp.speech_audio.AudioStream.data.length;
        const audioBuffer = new Uint8Array(resp.speech_audio.AudioStream.data);
        resp.speech_marks = generateVisemesFromTextAndAudio(inText, audioBufferLength, audioBuffer);
      } else {
        // Fallback to random speech marks if no audio data
        resp.speech_marks = createRandomSpeechMarks();
      }
    }

    return Promise.resolve(resp);
  })
  .catch(err => {
    if(retries){
      console.log("RETRYING TTS")
      return getTextToSpeech(args,retries-1);
    }else{
      return Promise.reject(err);
    }
  });
}

function splitLongAttributes(inString,inBasePropertyName){
  let outObject = {};
  let index = 0;
  let propertyValue = "";
  do{
    propertyValue = inString.substring(index*1000,(index*1000)+1000);

    if(propertyValue){
      outObject[inBasePropertyName + (index ? index : "")] = propertyValue;
    }
    index++;
  }while(propertyValue && (index < 5));

  return outObject;
}

// TODO: this is a stub for a real api call. For now it is returning dummy data.
export function logQuiz(data){
  return Promise.resolve({
    ...data,
    status: "completed",
  });
}

export function saveAssessmentTiming(inActivityId,inTimings,inRaw,inRetries=3){
  const options = {
    bucket: process.env.TIMINGBUCKET,
    contentType: 'application/json',
    level: "public",
    region: process.env.S3_BUCKET_REGION || 'us-east-2',
    customPrefix: {
      public: "",
    },
  };

  Storage.put(inActivityId+"_timing",JSON.stringify(inTimings),options)
  .then(()=>{
    if(inRaw) Storage.put(inActivityId+"_RAW",JSON.stringify(inRaw),options);
  })
  .catch((err) => {
    if(inRetries){
      console.log("RETRYING TIMING WRITE")
      logSessionInfo("retryTimingWrite",{error: err.message, options: options});
      saveAssessmentTiming(inActivityId,inTimings,inRaw,inRetries-1);
    }else{
      console.log("TIMING PUT FAIL:",err);
      logSessionInfo("timingWriteFail",{
        error: err.message,
        options: options,
        activityId: inActivityId,
        timings: inTimings,
        raw: inRaw
      });
    }
  });
}

export async function calculatePhraseSplits(timings,storyId,stopTime,shouldPad,storyObject,prevSlices = []){

  const callback = (story) => {
    const prevSlicesLength = prevSlices.length;
    const initialStartTime = prevSlicesLength > 0 ? prevSlices[prevSlicesLength - 1].end : 0;
    let slices = [...prevSlices, {phrase: prevSlicesLength, start: initialStartTime}];
    let phraseIndex = 0;
    let phrases = story.chapters[0].phrases;
    let nextPhraseBreak = phrases[phraseIndex].split(' ').length;
    let phraseBreaks = [nextPhraseBreak];
    //console.log("PHRASES:",phrases.length,JSON.stringify(phrases)); //Leave in for troubleshooting
    //console.log(JSON.stringify(timings)); //Leave in for troubleshooting
    for(let i=0;i<timings.length;i++){
      if(timings[i].position > nextPhraseBreak){
        if((i<timings.length - 1) && shouldPad){
          slices[prevSlicesLength + phraseIndex].end = timings[i+1].time;
        }
        else{
          if(shouldPad || (i === 0)){
            slices[prevSlicesLength + phraseIndex].end = timings[i].time;
          }else{
            slices[prevSlicesLength + phraseIndex].end = timings[i-1].time;
          }

        }
        phraseIndex++;
        if(i>0){
          if(i>1 && shouldPad){
            slices.push({phrase: prevSlicesLength + phraseIndex, start: timings[i-2].time});  //Enforce overlap
          }
          else{
            slices.push({phrase: prevSlicesLength + phraseIndex, start: timings[i-1].time});  //Enforce overlap
          }
        }else{
          slices.push({phrase: prevSlicesLength + phraseIndex, start: timings[i].time});  //Enforce overlap
        }
        if(phrases[phraseIndex]){
          nextPhraseBreak += phrases[phraseIndex].split(' ').length;
          phraseBreaks.push(nextPhraseBreak);
        }
      }
    }
    slices[prevSlicesLength + phraseIndex].end = stopTime;
    //console.log("slices:",JSON.stringify(slices));  //Leave in for troubleshooting

    if(slices.length - prevSlicesLength > phrases.length){
      slices = slices.slice(0,prevSlicesLength + phrases.length);
    }
    //console.log("phrase breaks:",JSON.stringify(phraseBreaks)); //Leave in for troubleshooting
    return Promise.resolve(slices);
  }

  if(storyObject){
    try {
      return callback(storyObject);
    } catch (error) {
      console.log("ERROR CALCULATING TIMINGS:",error);
      return Promise.reject(error);
    }
  }

  return getStory(storyId)
    .then(story =>{
      return callback(story);
    })
    .catch(err => {console.log("ERROR CALCULATING TIMINGS:",err); return Promise.reject(err);});
}

//Using this helper function because the studentIds coming from SIS
//have a different format than the ones coming from the activities call
//TODO: We should store the studentId the same way in SIS and "ActivitiesDB" so we don't have to format these ids
function getFormatedId(id) {
  return String(id).replace(/\-/g, '').toUpperCase();
}

export function getParentReport(studentId, equated=false) {
  return studentRecordService.getParentReport(studentId, equated).then((data) => {
    return data;
  }, (error) => {
    console.log(error);
    throw error
  });
}

export function getAllAssessmentsForStudents(students, limit=null, type=["assessment"], equated=false, locale=null, startDate, endDate) {
  return getActivitiesWithStudentInfo(students, limit, type, ["assigned","scored","rescored","not_started","in_progress","scoring","underReview","reassess"], equated, locale, startDate, endDate);
}

export function getValidDyslexiaAssessmentsForStudents(students, cachedDyslexiaData, limit=null) {
  if(cachedDyslexiaData && cachedDyslexiaData.activities){
    return Promise.resolve(cachedDyslexiaData.activities)
  }
  const studentIds = students.map(({ id }) => getFormatedId(id));
  return studentRecordService.dyslexiaActivities(studentIds, limit)
    .then((activities) => {
      return activities
    });
}

export function filterDyslexiaAssessments(students, activities, window){
  // all dyslexia activities within the school year
  if(!getUserData().demoLockdown){
    activities = filterByDate(activities, window);
  }

  if(Array.isArray(activities) && activities.length > 0){
    let englishActivities = students.map(({id})=>{
      return activities.find(activity => ((getFormatedId(id) == activity.studentId) && !isSpanishActivity(activity)));
    });
    englishActivities = englishActivities.filter(act=> act);

    let spanishActivities = students.map(({id})=>{
      return activities.find(activity => ((getFormatedId(id) == activity.studentId) && isSpanishActivity(activity)));
    });
    spanishActivities = spanishActivities.filter(act=> act);

    let returnActivities = englishActivities.concat(spanishActivities);
    //return the activities with the student information
    return returnActivities.map((activity) => ({
      ...activity,
      student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
    }));
  }
  return [];
}

export function isSpanishActivity(activity){
  return activity.tags && activity.tags.includes("ES_MX");
}

export function getValidActivitiesForStudents(students, limit=null, type=["assessment","tutor"], equated=false, locale=null, startDate, endDate) {
  if(students.length === 0){
    return Promise.resolve([]);
  }

  return getActivitiesWithStudentInfo(students, limit, type, ["scored","rescored"], equated, locale, startDate, endDate)
    .then((activities) => {
      console.log("getActivities",activities)
      return activities.filter((activity)=> activity && activity.scores && activity.scores.wcpmScore > 0 && activity.displayStatus !== statusType.UNASSIGNED);
    });
}

export function getParentActivitiesForStudents(students, limit=null, type=["assessment", "dyslexia", "letterNamingAndSounds"], equated=true, locale=null, startDate, endDate, statuses=null, displayStatuses=null, ) {
  if(students.length === 0){
    return Promise.resolve([]);
  }

  return getActivitiesWithStudentInfo(students, limit, type, statuses, equated, locale, startDate, endDate, displayStatuses)
    .then((activities) => {
      console.log("getActivities",activities)
      return activities.filter((activity)=> {
        // If the student is a prereader and has a dyslexia score, we want to show the dyslexia score
        if(activity?.displayStatus === 'prereader' &&
          activity?.children?.filter( childActivity => childActivity.type === 'dyslexia' &&
          isNotNullOrUndefined(childActivity.scores.armScore))?.length > 0){
          return true;
        }
        // Also want to handle the case where the student is a prereader and has a sibling with a dyslexia score (When taking )
        if(activity?.displayStatus === 'prereader' &&
          activity?.siblings?.filter( siblingActivity => siblingActivity.type === 'dyslexia' &&
          isNotNullOrUndefined(siblingActivity.scores.armScore))?.length > 0){
          return true;
        }
        return activity &&
          activity.scores &&
          activity.scores.wcpmScore > 0 &&
          activity.displayStatus !== statusType.UNASSIGNED
      });
    });
}

export function getProgressActivitiesForStudents(students, limit=null, type=["assessment","tutor","letterNamingAndSounds"], equated=false, locale, scale) {
  if(locale === locales.ES_MX) equated=false; //We don't support equating for spanish yet
  const studentIds = students.map(({ id }) => getFormatedId(id));
  const isARM = scale === "ARM";

  const currentSchoolYear = getSchoolYear();
  const { startDate, endDate } = currentSchoolYear

  return studentRecordService.progressActivities(studentIds, limit, type, equated, isARM, startDate, endDate)
    .then((activities) => {
      activities = filterByDate(activities);
      activities = filterByLocale(activities,locale);

      if(Array.isArray(activities) && activities.length > 0){
        //return the activities with the student information
        return activities.map((activity) => ({
          ...activity,
          student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
        }));
      }
      return [];
    }).then((activities) => {
      return activities.filter((activity)=> {
        const armScore = getArmScore(activity.student.id, activity, locale);
        const isValidARM = armScore || armScore == 0 || activity.type === assignmentTypes.LETTER_NAMING_AND_SOUNDS;
        return activity && activity.scores && ((activity.scores.wcpmScore > 0) || isScorableEarlyReaderStory(activity.story) || isValidARM);
      });
    });
}

export function getActivitiesWithStudentInfo(students, limit=null, type=["assessment","tutor"], status=["scored","rescored","assigned","not_started","in_progress","scoring","reassess"], equated=false, locale, startDate, endDate, displayStatus=null){
  if(locale === locales.ES_MX) equated=false; //We don't support equating for spanish yet
  const studentIds = students.map(({ id }) => getFormatedId(id));
  const startDateUTC = startDate && new Date(startDate).toISOString();
  const endDateUTC = endDate && new Date(endDate).toISOString();
  const localeEnum = locale ? formatLocale_UpperCaseUnderscore(locale) : null;
  return studentRecordService.studentActivities(studentIds, limit, type, status, equated, startDateUTC, endDateUTC, displayStatus, localeEnum)
    .then((activities) => {

      activities = filterByDate(activities);

      // fix the parent-child relationship issues
      if (getUserData().branding !== 'nwea-map-tutor'){
        activities = fixActivitiesParenting(activities);
      }


      if(locale){
        activities = filterByLocale(activities,locale);
      }
      //Take out assignment stubs
      activities = activities.filter((activity)=>activity.displayStatus !== statusType.UNASSIGNED);

      if(Array.isArray(activities) && activities.length > 0){
        //return the activities with the student information
        return activities.map((activity) => ({
          ...activity,
          student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
        }));
      }
      return [];
    });
}

function getDefaultScores(inGrade){
  const default_scores = [
    {
        'areaScore': 5.200691551661794,
        'decodeScore': 79.56625405844156,
        'esriScore': 68.81435846200803,
        'phonAwareScore': 70.46186794464171,
        'vocabSize': 1911.5625,
        'wcpmScore': 0.942,
        'armScore': 0
    },
    {
        'areaScore': 6.303115044093312,
        'decodeScore': 85.62736687243763,
        'esriScore': 97.2541197320828,
        'phonAwareScore': 86.0491521913347,
        'vocabSize': 4495.773148148148,
        'wcpmScore': 16.984,
        'armScore': 1
    },
    {
        'areaScore': 7.003687317038951,
        'decodeScore': 90.82779859969637,
        'esriScore': 98.59781172056606,
        'phonAwareScore': 91.59326475798281,
        'vocabSize': 6012.501992031873,
        'wcpmScore': 46.439,
        'armScore': 2
    },
    {
        'areaScore': 7.805100135704936,
        'decodeScore': 92.61302800358887,
        'esriScore': 99.72856285704745,
        'phonAwareScore': 93.21768266287745,
        'vocabSize': 7125.880952380952,
        'wcpmScore': 69.457,
        'armScore': 3
    },
    {
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 86.995,
      'armScore': 4
    },
    {
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 99.879,
      'armScore': 5
    },
    {
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 100.879,
      'armScore': 6
    },
    {
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 101.879,
      'armScore': 7
    },
    {
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 102.879,
      'armScore': 8
    },
    {
      'areaScore': 5.200691551661794,
      'decodeScore': 79.56625405844156,
      'esriScore': 68.81435846200803,
      'phonAwareScore': 70.46186794464171,
      'vocabSize': 1911.5625,
      'wcpmScore': 0.942,
      'armScore': 0
    }
  ];

  return inGrade === -1 ?
    default_scores[default_scores.length - 1] : // PreK is the last element
    (inGrade >= 0 && inGrade < default_scores.length - 2) ?
      default_scores[inGrade] : // Grades 0-8 map to indices 0-8
      default_scores[default_scores.length - 2]; // Default to Grade 8 for out-of-range values
}

// Whether endUserSession can close the current tab: it was script-opened (window.opener is set)
// and this isn't an iPad. Shared with willEndUserSessionStrand so the two can't drift.
function canCloseSessionTab(){
  return window.opener && !getUserData().sessionData.isiPad;
}

// Whether endUserSession, when it can't close the tab, routes through the trusted-origin redirect
// (SSO / skipMagic students) rather than reloading the page. Shared with willEndUserSessionStrand.
function sessionEndRedirectsToLoginOrigin(){
  return (userData_store && userData_store.skipMagic) && !userData_store.sideloadedUser;
}

export function endUserSession(forceLogout = false){
  if (canCloseSessionTab()) {
    window.open('', '_self').close();
  } else if (sessionEndRedirectsToLoginOrigin()) {
    redirectToLoginOrigin(null, forceLogout);
  } else {
    location.reload();
  }
}

// Side-effect-free mirror of endUserSession's branch logic: returns true when endUserSession would
// be unable to end the session — no window.opener to close the tab AND no trusted redirect to fall
// back on (e.g. HMH launcher students whose login_origin is not in the allowlist, which happens when
// a cross-origin COOP header strips window.opener). Reuses endUserSession's own branch predicates so
// the two stay in lock-step. Lets the completion screen show a "please close this tab" message
// immediately instead of waiting for the deferred endUserSession call to no-op.
export function willEndUserSessionStrand(){
  if (canCloseSessionTab()) return false;
  if (!sessionEndRedirectsToLoginOrigin()) return false; // falls to location.reload() — not stranded
  // endUserSession's completion caller never forces logout, so the redirect decision matches
  // redirectToLoginOrigin(null, false) via the shared wouldRedirectToLoginOrigin predicate.
  return !wouldRedirectToLoginOrigin(null, false);
}

export function underMaintenance(){
  window.location.href = "/underMaintenance.html";
}

let messageTimestamp = 0;
//https://stackoverflow.com/questions/28230845/communication-between-tabs-or-windows/28230846#28230846
// use local storage for messaging. Set message in local storage and clear it right away
// This is a safe way how to communicate with other tabs while not leaving any traces
//
export function message_broadcast(message)
{
  let messageToSend = {message: message};
  messageTimestamp = Date.now();
  messageToSend.timeStamp  = messageTimestamp;
  try{
    localStorage.setItem('amiraLogin',JSON.stringify(messageToSend));
  }catch(err){
    logSentryException(new Error('Message broadcast failure'), 'Error message_broadcast', { originalError: err?.message, originalStack: err?.stack });
    localStorage.removeItem('amiraLogin')
    try{
      localStorage.setItem('amiraLogin',JSON.stringify(messageToSend));
      console.log("could not shut down other windows");
    }catch(err2){
      logSentryException(new Error('Message broadcast retry failure'), 'Error message_broadcast2', { originalError: err2?.message, originalStack: err2?.stack });
    }
  }
  localStorage.removeItem('amiraLogin');

}


// receive message
//
export function message_receive(ev)
{
    if (!ev || ev.key!='amiraLogin') return; // ignore other keys
    var message=JSON.parse(ev.newValue);

    if (!message || (ev.oldValue && JSON.parse(ev.oldValue).timeStamp == messageTimestamp)) return; // ignore empty msg or msg reset

    if((message.message == 'logout') && (message.timeStamp > messageTimestamp)){
      console.log("End session because of other tab",ev);
      if(getUserData()){
        logSessionInfo("amira_session_launched_in_other_tab",{key: ev.key, value: ev.newValue, time: Date.now()})
        .then(()=>{
          endUserSession(true);
        })
        .catch((err)=>{
          console.log("ERROR",err);
          endUserSession()
        });
      }else{
        console.log("NO LOG - not signed in")
      }
    }
}

export function findFilenames(phonemes, inLocale){
  return getAudioForPhonemes(phonemes, inLocale ? inLocale : getUserData().locale);
}

export function findSyllableFilename(syllable, inLocale){
  return getAudioForSyllable(syllable, inLocale ? inLocale : getUserData().locale);
}
